import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import * as HiIcons from 'react-icons/hi';
import Select from './Select';
import {
  getStatusInfo,
  getStatusIcon,
  getStatusColor,
} from '../utils/statusConfig';
import apiService from '../services/api';
import { normalizeImageUrl } from '../utils/apiHelpers';
import {
  formatMoney,
  maskAmountInput,
  maskFromNumber,
  parseMaskedAmount,
  CURRENCY_META,
  isZeroDecimalCurrency,
} from '../utils/currencies';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import './Verify.css';

// Retorna o código de acesso direto de data.codigos_acesso (string ou primeiro disponível no objeto)
function getCodigoAcessoFromData(invoiceData) {
  if (!invoiceData) return '';
  let codigos = invoiceData.codigos_acesso;
  if (typeof codigos === 'string' && codigos.trim() !== '') {
    try {
      const parsed = JSON.parse(codigos);
      if (parsed && typeof parsed === 'object') codigos = parsed;
      else return codigos.trim();
    } catch (e) {
      return codigos.trim();
    }
  }
  if (!codigos || typeof codigos !== 'object') return '';
  return (
    codigos.administrador ||
    codigos.gerente ||
    codigos.transportador ||
    codigos.entregador ||
    ''
  );
}

const WITHDRAWAL_QR_READER_ID = 'withdrawal-qr-reader';
const WITHDRAWAL_PACKAGE_QR_READER_ID = 'withdrawal-package-qr-reader';
const CHECK_QR_READER_ID = 'rastreamento-check-qr-reader';
const CARGA_LABEL_QR_READER_ID = 'carga-label-qr-reader';

/** Extrai código do cliente a partir do texto bruto do QR (texto simples ou URL com ?code= / ?codigo=) */
function extractClientCodeFromScan(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const code =
        u.searchParams.get('code') ||
        u.searchParams.get('codigo') ||
        u.searchParams.get('codigo_cliente');
      if (code) return code.trim();
      const path = u.pathname.replace(/\/$/, '');
      const last = path.split('/').filter(Boolean).pop();
      if (last && last.length >= 2 && !/\.[a-z0-9]+$/i.test(last)) {
        return decodeURIComponent(last).trim();
      }
    } catch {
      return '';
    }
  }
  return s;
}

/** QR da retirada pode ser o código do cliente (CLI…) ou o código da etiqueta (CX/ML/PL/CT). */
function resolveCheckQrTarget(raw) {
  const text = String(raw ?? '')
    .replace(/[\u200b-\u200d\ufeff]/g, '')
    .trim();
  if (!text) return { code: '', tipo: '' };

  let tipo = '';
  let code = '';

  if (/^https?:\/\//i.test(text)) {
    try {
      const u = new URL(text);
      if ((u.searchParams.get('tipo') || '').toLowerCase() === 'cliente') {
        tipo = 'cliente';
      }
      code = (
        u.searchParams.get('codigo') ||
        u.searchParams.get('codigo_cliente') ||
        u.searchParams.get('code') ||
        u.searchParams.get('codigo_pacote') ||
        u.searchParams.get('pacote') ||
        ''
      ).trim();
      if (!code) {
        const last = u.pathname.split('/').filter(Boolean).pop() || '';
        code = decodeURIComponent(last).trim();
      }
    } catch {
      code = text;
    }
  }

  if (!code) {
    const compact = text.replace(/\s+/g, '');
    const match = compact.match(/((?:CLI|INV|CX|ML|PL|CT)[A-Z0-9]+)/i);
    code = (match ? match[1] : compact).trim();
  }

  if (!tipo && /^cli/i.test(code)) tipo = 'cliente';
  return { code, tipo };
}

/** Extrai código do pacote do QR (etiqueta costuma ser texto puro; URLs com codigo_pacote/pacote/code) */
function extractPackageCodeFromScan(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const code =
        u.searchParams.get('codigo_pacote') ||
        u.searchParams.get('pacote') ||
        u.searchParams.get('code') ||
        u.searchParams.get('codigo');
      if (code) return code.trim();
      const path = u.pathname.replace(/\/$/, '');
      const last = path.split('/').filter(Boolean).pop();
      if (last && last.length >= 2 && !/\.[a-z0-9]+$/i.test(last)) {
        return decodeURIComponent(last).trim();
      }
    } catch {
      return '';
    }
  }
  return s;
}

function getPackageCodesForMatch(packages) {
  if (!packages?.length) return [];
  return packages
    .map(pkg => {
      const c = pkg.codigo_pacote ?? pkg.codigo ?? pkg.id;
      return c != null ? String(c).trim() : '';
    })
    .filter(Boolean);
}

function itemPhotoSources(foto) {
  const full = normalizeImageUrl(foto?.url);
  const thumb = normalizeImageUrl(foto?.thumb_url) || full;
  return { full, thumb };
}

function codesEqualLoose(a, b) {
  const x = String(a ?? '').trim();
  const y = String(b ?? '').trim();
  if (!x || !y) return false;
  return x === y || x.toLowerCase() === y.toLowerCase();
}

function scannedPackageMatchesList(scannedRaw, packages) {
  const extracted = extractPackageCodeFromScan(scannedRaw);
  if (!extracted) return false;
  const expected = getPackageCodesForMatch(packages);
  return expected.some(exp => codesEqualLoose(extracted, exp));
}

function collectCaixaFisicaLookup(packages) {
  const byNumero = new Map();
  (packages || []).forEach(pkg => {
    const list = pkg.caixas_fisicas || [];
    list.forEach(caixa => {
      const numero = String(caixa?.numero ?? '').trim();
      if (!numero) return;
      const current = byNumero.get(numero) || { ...caixa, numero };
      if (caixa.misturado) current.misturado = true;
      const obs =
        caixa.observacao || caixa.observacao_pacote || caixa.observacao_caixa;
      if (obs && String(obs).trim()) {
        current.observacao = String(obs).trim();
      }
      byNumero.set(numero, current);
    });
  });
  const caixas = Array.from(byNumero.values());
  const temMisturado = caixas.some(c => Boolean(c.misturado));
  const temExclusivo = caixas.some(c => !c.misturado);
  return {
    caixas,
    numeros: caixas.map(c => String(c.numero)),
    temMisturado,
    temExclusivo,
    temSozinhoEMisturado: temMisturado && temExclusivo,
    emVarias: caixas.length > 1,
    observacoes: caixas
      .map(c => (c.observacao ? String(c.observacao).trim() : ''))
      .filter(Boolean),
    resumo: buildCaixaLocatorResumo(caixas),
  };
}

function buildCaixaLocatorResumo(caixas) {
  if (!caixas?.length) return null;
  if (caixas.length === 1) {
    const c = caixas[0];
    return c.misturado
      ? `Caixa ${c.numero} — está misturado com outros clientes`
      : `Caixa ${c.numero} — está inteiro na caixa (só este cliente)`;
  }
  return caixas
    .map(c =>
      c.misturado
        ? `Caixa ${c.numero} — misturado com outros clientes`
        : `Caixa ${c.numero} — sozinho (só este cliente)`
    )
    .join('; ');
}

function QrGlyph({ name }) {
  const props = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  if (name === 'code') {
    return (
      <svg {...props}>
        <path d="M4 5.5v13" strokeWidth="2.5" />
        <path d="M8 5.5v13" strokeWidth="1.15" />
        <path d="M11 5.5v13" strokeWidth="2.2" />
        <path d="M14.2 5.5v13" strokeWidth="1.15" />
        <path d="M17 5.5v13" strokeWidth="2.6" />
        <path d="M20.2 5.5v13" strokeWidth="1.25" />
      </svg>
    );
  }
  if (name === 'box') {
    return (
      <svg {...props}>
        <path d="M12 3.2 20.2 7.6v8.8L12 20.8 3.8 16.4V7.6L12 3.2z" />
        <path d="M12 12.1 20.2 7.6" />
        <path d="M12 12.1v8.7" />
        <path d="M12 12.1 3.8 7.6" />
      </svg>
    );
  }
  if (name === 'pin') {
    return (
      <svg {...props}>
        <path d="M12 21s6.2-5.1 6.2-10.1a6.2 6.2 0 1 0-12.4 0C5.8 15.9 12 21 12 21z" />
        <circle cx="12" cy="10.8" r="2.1" />
      </svg>
    );
  }
  if (name === 'doc') {
    return (
      <svg {...props}>
        <path d="M7 3.5h6.8L18 7.7V20.5H7z" />
        <path d="M13.8 3.5V8H18" />
        <path d="M9.5 12.5h5" />
        <path d="M9.5 16h5" />
      </svg>
    );
  }
  return (
    <svg {...props} width="22" height="22">
      <path d="M12 3.1c-1.9 0-3.4 1.2-3.4 2.7L6.1 9.2C5.1 10.8 4.5 12.4 4.5 14.1 4.5 18.3 7.7 21.2 12 21.2s7.5-2.9 7.5-7.1c0-1.7-.6-3.3-1.6-4.9L15.4 5.8c0-1.5-1.5-2.7-3.4-2.7z" />
      <text
        x="12"
        y="16.2"
        textAnchor="middle"
        fontSize="7.4"
        fontWeight="800"
        fill="currentColor"
        stroke="none"
        fontFamily="Outfit, ui-sans-serif, system-ui, sans-serif"
      >
        kg
      </text>
    </svg>
  );
}

function QrNote({ icon, label, value }) {
  return (
    <div className='qr-note'>
      <span className='qr-note__icon' aria-hidden>{icon}</span>
      <div>
        <strong>{label}</strong>
        <span>{value}</span>
      </div>
    </div>
  );
}

function CaixaFisicaLocator({ lookup, title = 'Onde procurar', compact = false, sheet = false }) {
  if (!lookup?.caixas?.length) {
    if (compact) return null;
    return (
      <div className='wd-locator wd-locator--empty'>
        <HiIcons.HiArchive size={18} />
        <span>Sem caixa física cadastrada para este CX</span>
      </div>
    );
  }

  const split = Boolean(lookup.temSozinhoEMisturado);
  const misturado = lookup.temMisturado && !lookup.temExclusivo;
  return (
    <div
      className={`wd-locator${sheet ? ' wd-locator--sheet' : ''}${
        split ? ' wd-locator--split' : misturado ? ' wd-locator--mix' : ' wd-locator--solo'
      }`}
    >
      <div className='wd-locator__head'>
        <span className='wd-locator__icon' aria-hidden>
          {sheet ? <HiIcons.HiCube size={20} /> : <HiIcons.HiArchive size={18} />}
        </span>
        <div>
          <p className='wd-locator__eyebrow'>{title}</p>
          <p className='wd-locator__resumo'>
            {lookup.resumo || buildCaixaLocatorResumo(lookup.caixas)}
          </p>
        </div>
      </div>
      <div className='wd-locator__boxes'>
        {lookup.caixas.map(caixa => {
          const mix = Boolean(caixa.misturado);
          const obs =
            caixa.observacao || caixa.observacao_pacote || caixa.observacao_caixa;
          return (
            <div
              key={caixa.id || caixa.numero}
              className={`wd-locator__box${mix ? ' is-mix' : ' is-solo'}`}
            >
              <span className='wd-locator__num'>Caixa {caixa.numero}</span>
              <span className={`wd-locator__badge${mix ? ' is-mix' : ''}`}>
                {sheet && mix ? <HiIcons.HiInformationCircle size={15} /> : null}
                {mix ? 'Misturado com outros clientes' : 'Só este cliente'}
              </span>
              {obs && String(obs).trim() ? (
                <p className='wd-locator__box-obs'>{String(obs).trim()}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function caixaLookupFromQr(caixa) {
  const caixas = caixa?.caixas_fisicas || [];
  const temMisturado = caixas.some(c => Boolean(c.misturado));
  const temExclusivo = caixas.some(c => !c.misturado);
  return {
    caixas,
    temMisturado,
    temExclusivo,
    temSozinhoEMisturado: temMisturado && temExclusivo,
    resumo: caixa?.resumo || buildCaixaLocatorResumo(caixas),
  };
}

function isWithdrawalPackageStepEligible(packages) {
  if (!packages?.length) return false;
  return packages.every(pkg => {
    const s = pkg.status?.toLowerCase() || '';
    return s !== 'retirado';
  });
}

function unpaidFreightPackages(packages) {
  return (packages || []).filter(pkg => {
    if (pkg?.entregue) return false;
    const status = String(pkg?.status || '').toLowerCase();
    if (status === 'entregue' || status === 'retirado') return false;
    if (pkg?.bloqueio_entrega_pagamento === true) return true;
    const pay = String(pkg?.status_pagamento || '').toLowerCase();
    return pay === 'pendente' || pay === 'incompleto';
  });
}

function freightHoldPackages(packages) {
  return unpaidFreightPackages(packages).filter(
    pkg => !pkg?.tem_pendencia_pagamento
  );
}

function pickupPendenciasFromPackages(packages) {
  return (packages || [])
    .filter(
      pkg => !pkg?.entregue && pkg?.tem_pendencia_pagamento && pkg?.pendencia_pagamento
    )
    .map(pkg => ({
      ...pkg.pendencia_pagamento,
      pacote_id: pkg.id,
      codigo_pacote: pkg.codigo_pacote,
      nome_destinatario: pkg.nome_destinatario,
    }));
}

function formatItemQty(value) {
  if (value == null || value === '') return '1';
  const normalized = String(value).trim().replace(',', '.');
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return String(value);
  if (Number.isInteger(amount)) return String(amount);
  return String(amount);
}

function formatEventoEm(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function QrLocalPayNotice({
  pendencia,
  extras,
  valor,
  photo,
  paying,
  canSubmit,
  submitLabel,
  onValorChange,
  onCamera,
  onFile,
  onClearPhoto,
  onSubmit,
  onNewScan,
}) {
  const moeda = String(pendencia?.moeda_local || 'XOF').toUpperCase();
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!(photo instanceof File)) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  return (
    <div className='qr-gate' role='dialog' aria-modal='true' aria-labelledby='qr-local-pay-title'>
      <div className='qr-gate__sheet'>
        <span className='qr-gate__icon qr-gate__icon--pay' aria-hidden>
          <HiIcons.HiCash size={26} />
        </span>
        <p className='qr-gate__eyebrow'>Retirada</p>
        <h2 id='qr-local-pay-title'>Valor a pagar no local</h2>
        <p className='qr-gate__copy'>
          Fotografe o dinheiro recebido para abater este valor. Só depois a próxima tela abre.
        </p>
        <div className='qr-gate__amount'>
          <span>Valor a cobrar</span>
          <strong>{formatMoney(pendencia?.valor_local, moeda)}</strong>
          <em>{moeda}</em>
        </div>
        {(pendencia?.codigo_pacote || pendencia?.nome_destinatario) && (
          <p className='qr-gate__meta'>
            {pendencia.codigo_pacote ? <strong>{pendencia.codigo_pacote}</strong> : null}
            {pendencia.nome_destinatario ? <span>{pendencia.nome_destinatario}</span> : null}
          </p>
        )}
        {extras > 0 ? (
          <p className='qr-gate__more'>
            +{extras} pendência{extras > 1 ? 's' : ''} no local
          </p>
        ) : null}
        <label className='qr-gate__field' htmlFor='qr-local-pay-valor'>
          Valor recebido ({moeda})
          <input
            id='qr-local-pay-valor'
            type='text'
            inputMode='decimal'
            value={valor}
            onChange={event => onValorChange(event.target.value)}
            autoComplete='off'
          />
        </label>
        <div className='qr-gate__photos'>
          <button type='button' className='qr-gate__photo-btn' onClick={onCamera}>
            <HiIcons.HiCamera size={18} />
            Fotografar dinheiro
          </button>
          <label className='qr-gate__photo-btn'>
            <HiIcons.HiCloudUpload size={18} />
            Anexar
            <input
              type='file'
              accept='image/*'
              capture='environment'
              onChange={event => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) onFile(file);
              }}
            />
          </label>
        </div>
        {preview ? (
          <div className='qr-gate__preview'>
            <img src={preview} alt='Dinheiro recebido' />
            <button type='button' onClick={onClearPhoto} aria-label='Remover foto'>
              <HiIcons.HiX size={16} />
            </button>
          </div>
        ) : (
          <p className='qr-gate__photo-hint'>A foto do dinheiro é obrigatória para abater.</p>
        )}
        <button
          type='button'
          className='btn btn-primary qr-check-again'
          disabled={!canSubmit || paying}
          onClick={onSubmit}
        >
          {paying ? 'Registrando…' : submitLabel}
        </button>
        <button type='button' className='btn qr-check-again qr-new-search' onClick={onNewScan}>
          <HiIcons.HiSearch size={18} />
          Nova Busca
        </button>
      </div>
    </div>
  );
}

function QrFinanceHold({ pendencia, onNewScan }) {
  const remetente = pendencia?.remetente || {};
  const telefone = remetente.telefone ? String(remetente.telefone) : '';
  return (
    <div className='qr-result'>
      <section className='qr-finance' role='alert' aria-labelledby='qr-finance-title'>
        <span className='qr-gate__icon qr-gate__icon--hold' aria-hidden>
          <HiIcons.HiExclamationCircle size={26} />
        </span>
        <p className='qr-gate__eyebrow'>Bloqueio</p>
        <h2 id='qr-finance-title'>Pendência Financeira</h2>
        <p className='qr-gate__copy'>
          O remetente precisa resolver esta pendência antes de liberar as demais informações da carga.
        </p>
        {pendencia?.valor_restante != null ? (
          <div className='qr-gate__amount'>
            <span>Valor restante</span>
            <strong>{formatMoney(pendencia.valor_restante, 'BRL')}</strong>
            {pendencia.status_pagamento_label ? <em>{pendencia.status_pagamento_label}</em> : null}
          </div>
        ) : null}
        <div className='qr-finance__who'>
          <span>Remetente</span>
          <strong>{remetente.nome || 'Não informado'}</strong>
          {telefone ? (
            <a href={`tel:${telefone.replace(/\s/g, '')}`}>{telefone}</a>
          ) : null}
          {pendencia?.codigo_pacote ? <em>{pendencia.codigo_pacote}</em> : null}
        </div>
      </section>
      <button type='button' className='btn qr-check-again qr-new-search' onClick={onNewScan}>
        <HiIcons.HiSearch size={18} />
        Nova Busca
      </button>
    </div>
  );
}

function QrCheckResult({
  qrCheck,
  boxData,
  invoiceData,
  onNewScan,
  onCheckCarga,
  localPay,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const financeiro = qrCheck?.financeiro || null;
  const pendenciaLocal = financeiro?.pendencia_local || null;
  const pendenciaFinanceira = financeiro?.pendencia_financeira || null;
  const bloqueiaInformacoes = Boolean(financeiro?.bloqueia_informacoes);
  const pickupReady = Boolean(qrCheck?.disponivel_retirada);
  const statusKey = pickupReady
    ? 'disponivel_retirada'
    : qrCheck?.status === 'entregue'
      ? 'entrega_confirmada'
      : qrCheck?.status || 'pedido_criado';
  const statusInfo = getStatusInfo(statusKey);
  const StatusIcon = HiIcons[getStatusIcon(statusKey)] || HiIcons.HiClock;
  const color = getStatusColor(statusKey);
  const statusLabel = qrCheck?.status_label || statusInfo.label;
  const caixaLookup = caixaLookupFromQr(qrCheck?.caixa);
  const qrItens = Array.isArray(qrCheck?.itens) ? qrCheck.itens : [];
  const packageCodes = new Set(
    qrItens.map(item => item.codigo_pacote).filter(Boolean)
  );
  const showPackageCode = packageCodes.size > 1;
  const consultaInvoice = qrCheck?.origem_consulta === 'invoice';
  const tipo = String(boxData?.tipo || '').toLowerCase();
  const tipoLabel = consultaInvoice
    ? 'Remessa'
    : tipo === 'caixa'
      ? 'Caixa'
      : tipo === 'mala'
        ? 'Mala'
        : boxData?.tipo || 'Pacote';
  const codigoExibido = consultaInvoice
    ? invoiceData?.numero_documento || qrCheck?.codigo_consultado
    : boxData?.codigo || qrCheck?.codigo_consultado;
  const evento = formatEventoEm(qrCheck?.evento_em);
  const localizacao = qrCheck?.localizacao || 'Localização não informada';
  const descricao = qrCheck?.descricao || statusLabel;
  const origemInfo = (
    <div className='qr-after-caixa'>
      <QrNote
        icon={<HiIcons.HiUser size={18} />}
        label='Nome do Remetente'
        value={qrCheck?.pacote?.nome_remetente || 'Não informado'}
      />
    </div>
  );

  if (bloqueiaInformacoes) {
    return (
      <QrFinanceHold pendencia={pendenciaFinanceira} onNewScan={onNewScan} />
    );
  }

  if (pendenciaLocal) {
    const extras = Math.max(0, (financeiro?.pendencias_locais?.length || 1) - 1);
    return (
      <QrLocalPayNotice
        pendencia={pendenciaLocal}
        extras={extras}
        valor={localPay?.valor || ''}
        photo={localPay?.photo || null}
        paying={Boolean(localPay?.paying)}
        canSubmit={Boolean(localPay?.canSubmit)}
        submitLabel={localPay?.submitLabel || 'Abater e continuar'}
        onValorChange={localPay?.onValorChange}
        onCamera={localPay?.onCamera}
        onFile={localPay?.onFile}
        onClearPhoto={localPay?.onClearPhoto}
        onSubmit={localPay?.onSubmit}
        onNewScan={onNewScan}
      />
    );
  }

  return (
    <div className='qr-result'>
      <div className='verify-status'>
        <div
          className='status-badge'
          style={{ backgroundColor: `${color}20`, color }}
        >
          <StatusIcon size={16} />
          <span>{statusLabel}</span>
        </div>
      </div>

      <div className='verify-section'>
        <h2>
          {pickupReady ? 'Informações' : consultaInvoice ? 'Remessa' : 'Pacote'}
        </h2>
        <div className='qr-destinatario'>
          <img className='qr-destinatario__mark' src='/SENDING_TO.png' alt='' />
          <strong>Nome do Destinatário</strong>
          <span>{qrCheck?.pacote?.nome_destinatario || 'Não informado'}</span>
        </div>
        <div className='qr-meta-row'>
          <div className='qr-chip'>
            <span className='qr-chip__icon'><QrGlyph name='code' /></span>
            <strong>Código</strong>
            <span>{codigoExibido || '—'}</span>
          </div>
          <div className='qr-chip'>
            <span className='qr-chip__icon'><QrGlyph name='box' /></span>
            <strong>Tipo</strong>
            <span>{tipoLabel}</span>
          </div>
          <div className='qr-chip'>
            <span className='qr-chip__icon'><QrGlyph name='weight' /></span>
            <strong>Peso</strong>
            <span>{!consultaInvoice && boxData?.peso ? boxData.peso : '—'}</span>
          </div>
        </div>
      </div>

      {pickupReady ? (
        <>
          <div className='verify-section'>
            <h2>Caixa</h2>
            <CaixaFisicaLocator lookup={caixaLookup} title='Caixa física' sheet />
            {origemInfo}
          </div>

          <div className='verify-section'>
            <h2>
              {consultaInvoice ? 'Itens da remessa' : 'Itens'} ({qrItens.length})
            </h2>
            {qrItens.length > 0 ? (
              <div className='qr-item-list'>
                {qrItens.map(item => {
                  const fotos = Array.isArray(item.fotos) ? item.fotos : [];
                  return (
                    <article key={`${item.pacote_id || 'p'}-${item.id}`} className='qr-item-card'>
                      <div className='qr-item-card__main'>
                        <h3>{item.nome || 'Item sem nome'}</h3>
                        {showPackageCode && item.codigo_pacote ? (
                          <p className='qr-item-card__pkg'>{item.codigo_pacote}</p>
                        ) : null}
                      </div>
                      <span className='qr-item-qty'>Qtd {formatItemQty(item.quantidade)}</span>
                      {fotos.length > 0 ? (
                        <div className='qr-item-photos'>
                          {fotos.map(foto => {
                            const { full, thumb } = itemPhotoSources(foto);
                            if (!thumb) return null;
                            return (
                              <button
                                key={foto.id || thumb}
                                type='button'
                                className='qr-item-photo'
                                onClick={() => full && setPreviewUrl(full)}
                              >
                                <img src={thumb} alt={item.nome || 'Foto do item'} />
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className='qr-item-card__nofoto'>Sem foto</p>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className='qr-check-empty'>Nenhum item cadastrado neste pacote.</p>
            )}
          </div>
        </>
      ) : (
        <>
        <section className='qr-track' aria-label='Rastreio'>
          <div className='qr-track__row'>
            <span className='qr-track__icon qr-track__icon--pin' aria-hidden>
              <QrGlyph name='pin' />
            </span>
            <div>
              <strong>Localização</strong>
              <p>{localizacao}</p>
            </div>
          </div>
          <div className='qr-track__row'>
            <span className='qr-track__icon' aria-hidden>
              <QrGlyph name='doc' />
            </span>
            <div>
              <strong>Descrição</strong>
              <p>{descricao}</p>
            </div>
          </div>
          {evento ? (
            <p className='qr-track__when'>
              <HiIcons.HiClock size={15} />
              {evento}
            </p>
          ) : null}
        </section>
        {origemInfo}
        </>
      )}

      {pickupReady ? (
        <button type='button' className='btn btn-primary qr-check-again' onClick={onCheckCarga}>
          <HiIcons.HiQrcode size={18} />
          Check da Carga
        </button>
      ) : null}

      <button type='button' className='btn qr-check-again qr-new-search' onClick={onNewScan}>
        <HiIcons.HiSearch size={18} />
        Nova Busca
      </button>

      {previewUrl ? (
        <button
          type='button'
          className='qr-photo-preview'
          onClick={() => setPreviewUrl(null)}
          aria-label='Fechar foto'
        >
          <img src={previewUrl} alt='Foto do item' />
        </button>
      ) : null}
    </div>
  );
}

const Verify = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('code');
  const tipoConsulta = searchParams.get('tipo');

  const [boxData, setBoxData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullCode, setFullCode] = useState('');
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalData, setWithdrawalData] = useState({
    numero_documento: '',
    codigo_acesso: '',
    codigo_cliente: '',
  });
  const [clientPackages, setClientPackages] = useState([]);
  const [verifyingClient, setVerifyingClient] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null); // Mensagem de retorno da verificação
  const [verificationMessageType, setVerificationMessageType] = useState(null); // 'success', 'error', 'warning'
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [reportData, setReportData] = useState({
    tipo: '',
    descricao: '',
    fotos: [],
  });
  const [reportingPackage, setReportingPackage] = useState(false);
  const [showViewReportsModal, setShowViewReportsModal] = useState(false);
  const [selectedPackageForReports, setSelectedPackageForReports] =
    useState(null);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [pendingPaymentGate, setPendingPaymentGate] = useState([]); // pendências abertas
  const [showPendingPaymentModal, setShowPendingPaymentModal] = useState(false);
  const [pendingPaymentPhoto, setPendingPaymentPhoto] = useState(null);
  const [pendingPaymentValor, setPendingPaymentValor] = useState('');
  const [payingPendencia, setPayingPendencia] = useState(false);
  const [freightPaymentHold, setFreightPaymentHold] = useState([]);
  const [showFreightHoldModal, setShowFreightHoldModal] = useState(false);
  const [withdrawalRemetente, setWithdrawalRemetente] = useState(null);
  const [withdrawalFormData, setWithdrawalFormData] = useState({
    foto_saida: null,
    foto_assinatura: null,
    foto_documento: null,
  });
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [withdrawalInputMode, setWithdrawalInputMode] = useState('qr'); // 'qr' | 'manual'
  const [withdrawalQrError, setWithdrawalQrError] = useState(null);
  const [withdrawalQrScannerNonce, setWithdrawalQrScannerNonce] = useState(0);
  const [withdrawalPackageQrVerified, setWithdrawalPackageQrVerified] =
    useState(false);
  const [withdrawalPackageQrError, setWithdrawalPackageQrError] =
    useState(null);
  const [withdrawalPackageInputMode, setWithdrawalPackageInputMode] =
    useState('qr');
  const [withdrawalPackageManualCode, setWithdrawalPackageManualCode] =
    useState('');
  const [packageQrScannerNonce, setPackageQrScannerNonce] = useState(0);
  /** Só liga a câmera do QR do pacote após o utilizador clicar em "Confirmar pacote" */
  const [withdrawalPackageQrScanActive, setWithdrawalPackageQrScanActive] =
    useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const signatureCanvasRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const qrScanHandledRef = useRef(false);
  const verifyWithdrawalClientRef = useRef(null);
  const packageHtml5QrCodeRef = useRef(null);
  const packageQrScanHandledRef = useRef(false);
  const clientPackagesRef = useRef([]);
  const [qrCheck, setQrCheck] = useState(null);
  const [showCheckQrScanner, setShowCheckQrScanner] = useState(false);
  const [checkQrError, setCheckQrError] = useState(null);
  const [checkQrManualCode, setCheckQrManualCode] = useState('');
  const [checkQrInputMode, setCheckQrInputMode] = useState('qr');
  const [checkQrScannerNonce, setCheckQrScannerNonce] = useState(0);
  const checkQrHtml5Ref = useRef(null);
  const checkQrHandledRef = useRef(false);
  const checkQrSettledRef = useRef('');
  const cargaQrRef = useRef(null);
  const cargaScanHandledRef = useRef(false);
  const [cargaStep, setCargaStep] = useState(null);
  const [cargaScanError, setCargaScanError] = useState(null);
  const [cargaMismatchOpen, setCargaMismatchOpen] = useState(false);
  const [cargaInputMode, setCargaInputMode] = useState('qr');
  const [cargaManualCode, setCargaManualCode] = useState('');
  const [cargaScannerNonce, setCargaScannerNonce] = useState(0);
  const [cargaPreviewUrl, setCargaPreviewUrl] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraField, setCameraField] = useState(null); // 'foto_saida' ou 'foto_documento' ou 'report_foto'
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' = traseira, 'user' = frontal
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const {
    notifications,
    removeNotification,
    showWarning,
    showError,
    showSuccess,
  } = useNotification();

  clientPackagesRef.current = clientPackages;

  const withdrawalPackagesStepKey = useMemo(
    () =>
      clientPackages
        .map(p =>
          [p.id, p.codigo_pacote, p.codigo, p.status].map(String).join(':')
        )
        .join('|'),
    [clientPackages]
  );

  // Função para comprimir imagem
  const compressImage = (
    file,
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.6
  ) => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar se necessário
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Converter para blob comprimido
          canvas.toBlob(
            blob => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const locationFromClientHistory = async codigoCliente => {
    const code = String(codigoCliente || '').trim();
    if (!code) return null;
    try {
      const track = await apiService.getTrackingByClientCode(code);
      const historico = track?.data?.historico || [];
      for (const item of historico) {
        const local = String(item?.local_pacote || '').trim();
        if (local) return local;
      }
    } catch {
      return null;
    }
    return null;
  };

  const applyQrCheckData = data => {
    setQrCheck(data);
    setBoxData({
      codigo: data.pacote?.codigo_pacote || data.codigo_consultado,
      tipo: data.pacote?.tipo || 'caixa',
      status: data.status,
      peso: data.pacote?.peso_bruto,
      cliente_nome: data.pacote?.cliente_nome || 'Não informado',
    });
    if (data.invoice) {
      setInvoiceData({
        numero_documento: data.invoice.numero_documento,
        codigo_rastreamento: data.invoice.numero_documento,
        origem: data.invoice.origem,
        destino: data.invoice.destino,
        status_invoice: data.invoice.status_invoice || data.status_label,
        cliente_destinatario: data.pacote?.cliente_nome,
        codigos_acesso: data.invoice.codigos_acesso,
      });
      const acesso = getCodigoAcessoFromData({
        codigos_acesso: data.invoice.codigos_acesso,
      });
      setFullCode(acesso || '');
    } else {
      setInvoiceData(null);
      setFullCode('');
    }
  };

  const loadCheckQr = async (scannedCode, tipo) => {
    const lookup = lookupTipo =>
      apiService.checkQrCode(scannedCode, lookupTipo === 'cliente' ? 'cliente' : null);
    try {
      setLoading(true);
      setError(null);
      let resolvedTipo = tipo === 'cliente' ? 'cliente' : '';
      let response;
      try {
        response = await lookup(resolvedTipo);
      } catch (err) {
        const missing = /n[aã]o encontrado/i.test(err?.message || '');
        if (!missing) throw err;
        const other = resolvedTipo === 'cliente' ? '' : 'cliente';
        try {
          response = await lookup(other);
          resolvedTipo = other;
        } catch {
          throw err;
        }
      }
      let data = response?.data || response;
      if (data && !data.disponivel_retirada) {
        const clientCode =
          (resolvedTipo === 'cliente' ? scannedCode : '') ||
          data?.pacote?.codigo_cliente ||
          '';
        const localHistorico = await locationFromClientHistory(clientCode);
        if (localHistorico) {
          data = { ...data, localizacao: localHistorico };
        }
      }
      if (!data) {
        setError(
          tipo === 'cliente'
            ? 'Cliente não encontrado para este código'
            : 'Pacote não encontrado para este QR Code'
        );
        setQrCheck(null);
        setLoading(false);
        return;
      }
      applyQrCheckData(data);
      const settledTipo = resolvedTipo === 'cliente' ? 'cliente' : '';
      if ((tipo === 'cliente' ? 'cliente' : '') !== settledTipo) {
        checkQrSettledRef.current = `${scannedCode}|${settledTipo}`;
        setSearchParams(
          settledTipo ? { code: scannedCode, tipo: 'cliente' } : { code: scannedCode },
          { replace: true }
        );
      }
      setLoading(false);
    } catch (err) {
      console.error('Erro ao conferir QR Code:', err);
      setError(err.message || 'Erro ao conferir QR Code');
      setQrCheck(null);
      setBoxData(null);
      setInvoiceData(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    const settledKey = `${code || ''}|${tipoConsulta === 'cliente' ? 'cliente' : ''}`;
    if (code && checkQrSettledRef.current === settledKey) {
      checkQrSettledRef.current = '';
      return;
    }
    if (code) {
      loadCheckQr(code, tipoConsulta);
    } else {
      setLoading(false);
      setQrCheck(null);
      setBoxData(null);
      setInvoiceData(null);
    }
  }, [code, tipoConsulta]);

  const stopCheckQrScanner = async () => {
    const instance = checkQrHtml5Ref.current;
    checkQrHtml5Ref.current = null;
    if (!instance) return;
    try {
      await instance.stop();
    } catch {
      /* já parado */
    }
    try {
      instance.clear();
    } catch {
      /* ignore */
    }
  };

  const handleCheckQrDecoded = decodedText => {
    const { code: extracted, tipo } = resolveCheckQrTarget(decodedText);
    if (!extracted) {
      showWarning('QR lido, mas não foi possível extrair o código.');
      return;
    }
    checkQrHandledRef.current = true;
    void stopCheckQrScanner();
    setShowCheckQrScanner(false);
    setSearchParams(
      tipo === 'cliente' ? { code: extracted, tipo: 'cliente' } : { code: extracted }
    );
  };

  const handleCheckQrManualSubmit = e => {
    e.preventDefault();
    const extracted =
      extractClientCodeFromScan(checkQrManualCode) ||
      String(checkQrManualCode || '').trim();
    if (!extracted) {
      showError('Informe o código do cliente');
      return;
    }
    setShowCheckQrScanner(false);
    setSearchParams({ code: extracted, tipo: 'cliente' });
  };

  useEffect(() => {
    if (!showCheckQrScanner || checkQrInputMode !== 'qr') {
      void stopCheckQrScanner();
      return undefined;
    }

    let cancelled = false;
    setCheckQrError(null);
    checkQrHandledRef.current = false;

    const start = async () => {
      await stopCheckQrScanner();
      if (cancelled) return;
      const el = document.getElementById(CHECK_QR_READER_ID);
      if (!el) return;

      const html5QrCode = new Html5Qrcode(CHECK_QR_READER_ID);
      checkQrHtml5Ref.current = html5QrCode;
      const config = {
        fps: 10,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const edge = Math.min(viewfinderWidth, viewfinderHeight);
          const size = Math.max(160, Math.floor(edge * 0.72));
          return { width: size, height: size };
        },
      };

      try {
        await html5QrCode.start(
          { facingMode: 'environment' },
          config,
          decodedText => {
            if (cancelled || checkQrHandledRef.current) return;
            handleCheckQrDecoded(decodedText);
          },
          () => undefined
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Check QR scanner:', err);
          setCheckQrError('Câmera indisponível. Digite o código.');
          setCheckQrInputMode('manual');
        }
      }
    };

    void start();
    return () => {
      cancelled = true;
      void stopCheckQrScanner();
    };
  }, [showCheckQrScanner, checkQrInputMode, checkQrScannerNonce]);

  const formatPendenciaLocal = (pend) =>
    formatMoney(pend?.valor_local, pend?.moeda_local || 'XOF');

  const formatPendenciaCurrencyCode = (pend) =>
    String(pend?.moeda_local || 'XOF').toUpperCase();

  const pendingCurrency = (pend) =>
    String(pend?.moeda_local || 'XOF').toUpperCase();

  const pendingCurrencySymbol = (pend) => {
    const code = pendingCurrency(pend);
    return CURRENCY_META[code]?.symbol || code;
  };

  const formatBrlMoney = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Number(value) || 0);

  const formatPendingLocalInput = (raw, currency) => {
    const masked = maskAmountInput(raw, currency);
    if (!masked) return '';
    const symbol = CURRENCY_META[currency]?.symbol || currency;
    if (currency === 'EUR') return `€ ${masked}`;
    if (currency === 'USD') return `US$ ${masked}`;
    return `${symbol} ${masked}`;
  };

  const parsePendingLocalInput = (value, currency) => {
    // Remove símbolo / código e parseia máscara pt-BR
    const cleaned = String(value || '')
      .replace(/US\$/gi, '')
      .replace(/R\$/gi, '')
      .replace(/€/g, '')
      .replace(/\b(CFA|FCFA|Kz|CVE|FC|XOF|XAF|AOA|CDF|EUR|USD|BRL)\b/gi, '')
      .trim();
    const parsed = parseMaskedAmount(cleaned, currency);
    return parsed == null ? 0 : parsed;
  };

  const prefillPendingPaymentValor = (pend) => {
    const currency = pendingCurrency(pend);
    const local = Number(pend?.valor_local) || 0;
    if (!(local > 0)) {
      setPendingPaymentValor('');
      return;
    }
    const masked = maskFromNumber(local, currency);
    const symbol = CURRENCY_META[currency]?.symbol || currency;
    if (currency === 'EUR') {
      setPendingPaymentValor(`€ ${masked}`);
      return;
    }
    if (currency === 'USD') {
      setPendingPaymentValor(`US$ ${masked}`);
      return;
    }
    setPendingPaymentValor(`${symbol} ${masked}`);
  };

  useEffect(() => {
    const pend = qrCheck?.financeiro?.pendencia_local;
    if (!pend || qrCheck?.financeiro?.bloqueia_informacoes) return;
    const locais = Array.isArray(qrCheck.financeiro.pendencias_locais) &&
      qrCheck.financeiro.pendencias_locais.length
      ? qrCheck.financeiro.pendencias_locais
      : [pend];
    setPendingPaymentGate(locais);
    setPendingPaymentPhoto(null);
    prefillPendingPaymentValor(pend);
  }, [
    qrCheck?.codigo_consultado,
    qrCheck?.financeiro?.bloqueia_informacoes,
    qrCheck?.financeiro?.pendencia_local?.id,
    qrCheck?.financeiro?.pendencia_local?.valor_local,
  ]);

  /** Converte valor local digitado para BRL proporcional à pendência. */
  const localAmountToBrl = (localAmount, pend) => {
    const localMax = Number(pend?.valor_local) || 0;
    const brlMax = Number(pend?.valor_brl) || 0;
    if (!(localAmount > 0) || !(localMax > 0) || !(brlMax > 0)) return 0;
    const eps = isZeroDecimalCurrency(pendingCurrency(pend)) ? 0.5 : 0.009;
    if (Math.abs(localAmount - localMax) <= eps) {
      return Math.round(brlMax * 100) / 100;
    }
    return Math.round((localAmount / localMax) * brlMax * 100) / 100;
  };

  const refreshClientPackagesForWithdrawal = async () => {
    const codigo_cliente = (withdrawalData.codigo_cliente || '').trim();
    const codigo_acesso = (withdrawalData.codigo_acesso || '').trim();
    const numero_documento = (withdrawalData.numero_documento || '').trim();
    if (!codigo_cliente || !codigo_acesso || !numero_documento) return null;
    try {
      const response = await apiService.verifyClientPackages({
        codigo_cliente,
        codigo_acesso,
        numero_documento,
      });
      if (response?.status === 'success') {
        const pacotes = response.pacotes || response.data?.pacotes || [];
        setClientPackages(pacotes);
        const remetente = response.remetente || response.cliente;
        if (remetente) setWithdrawalRemetente(remetente);
        return { pacotes, response };
      }
    } catch (error) {
      console.error('Erro ao atualizar pacotes após pagamento:', error);
    }
    return null;
  };

  const resetWithdrawalPackageQrStep = () => {
    setWithdrawalPackageQrVerified(false);
    setWithdrawalPackageQrError(null);
    setWithdrawalPackageInputMode('qr');
    setWithdrawalPackageManualCode('');
    setPackageQrScannerNonce(0);
    packageQrScanHandledRef.current = false;
    setWithdrawalPackageQrScanActive(false);
  };

  const applyWithdrawalPaymentGates = (pacotes, response) => {
    const remetente = response?.remetente || response?.cliente;
    if (remetente) setWithdrawalRemetente(remetente);

    const pendencias =
      response?.pendencias_pagamento ||
      response?.data?.pendencias_pagamento ||
      pickupPendenciasFromPackages(pacotes);

    if (Array.isArray(pendencias) && pendencias.length > 0) {
      setPendingPaymentGate(pendencias);
      setPendingPaymentPhoto(null);
      prefillPendingPaymentValor(pendencias[0]);
      setShowPendingPaymentModal(true);
      setShowWithdrawalForm(false);
      setFreightPaymentHold([]);
      setShowFreightHoldModal(false);
      resetWithdrawalPackageQrStep();
      return;
    }

    setPendingPaymentGate([]);
    setShowPendingPaymentModal(false);
    setShowWithdrawalForm(true);
    resetWithdrawalPackageQrStep();

    const hold = freightHoldPackages(pacotes);
    if (hold.length > 0) {
      setFreightPaymentHold(hold);
      setShowFreightHoldModal(true);
      return;
    }

    setFreightPaymentHold([]);
    setShowFreightHoldModal(false);
  };

  const formatPaymentDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const verifyWithdrawalClient = async (e, codigoClienteOverride) => {
    if (e?.preventDefault) e.preventDefault();

    const codigo_cliente = (
      codigoClienteOverride ??
      withdrawalData.codigo_cliente ??
      ''
    ).trim();
    const codigo_acesso = (withdrawalData.codigo_acesso || '').trim();
    const numero_documento = (withdrawalData.numero_documento || '').trim();

    if (!codigo_cliente || !codigo_acesso || !numero_documento) {
      showWarning(
        'Informe o código do cliente (QR ou manual) e confira documento e código de acesso.'
      );
      return;
    }

    try {
      setVerifyingClient(true);
      setWithdrawalData(prev => ({ ...prev, codigo_cliente }));
      const response = await apiService.verifyClientPackages({
        codigo_cliente,
        codigo_acesso,
        numero_documento,
      });

      if (response && response.status === 'success') {
        // Salvar código de acesso no localStorage
        if (codigo_acesso) {
          localStorage.setItem('lastAccessCode', codigo_acesso);
        }

        // A resposta já vem com pacotes e cliente no nível raiz
        // Estrutura: { status: "success", numero_documento: "...", cliente: {...}, pacotes: [...] }
        const pacotes = response.pacotes || response.data?.pacotes || [];
        const message = response.message || response.msg || '';

        setClientPackages(pacotes);

        const remetente = response.remetente || response.cliente;
        if (remetente) setWithdrawalRemetente(remetente);

        // Exibir mensagem de retorno apenas se houver mensagem da API ou se não houver pacotes
        if (message) {
          setVerificationMessage(message);
          setVerificationMessageType('success');
        } else if (pacotes.length === 0) {
          setVerificationMessage('Nenhum pacote encontrado para este cliente.');
          setVerificationMessageType('warning');
        } else {
          // Não exibir mensagem quando pacotes são encontrados
          setVerificationMessage(null);
          setVerificationMessageType(null);
        }

        applyWithdrawalPaymentGates(pacotes, response);
      } else {
        const errorMsg =
          response?.message ||
          response?.msg ||
          'Não foi possível encontrar pacotes para este cliente';
        setVerificationMessage(errorMsg);
        setVerificationMessageType('error');
        setClientPackages([]);
        setShowWithdrawalForm(false);
        showError(errorMsg);
        qrScanHandledRef.current = false;
        setWithdrawalQrScannerNonce(n => n + 1);
      }
    } catch (error) {
      console.error('Erro ao verificar cliente:', error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        'Erro ao verificar pacotes do cliente';
      setVerificationMessage(errorMsg);
      setVerificationMessageType('error');
      setClientPackages([]);
      setShowWithdrawalForm(false);
      showError(errorMsg);
      qrScanHandledRef.current = false;
      setWithdrawalQrScannerNonce(n => n + 1);
    } finally {
      setVerifyingClient(false);
    }
  };

  verifyWithdrawalClientRef.current = verifyWithdrawalClient;

  const handleConfirmPendingPayment = async () => {
    const current = pendingPaymentGate[0];
    if (!current?.id) {
      showWarning('Nenhuma pendência para quitar');
      return;
    }
    if (!(pendingPaymentPhoto instanceof File)) {
      showWarning('Tire ou anexe a foto do pagamento antes de continuar');
      return;
    }
    const currency = pendingCurrency(current);
    const maxLocal = Number(current.valor_local) || 0;
    const valorLocal = parsePendingLocalInput(pendingPaymentValor, currency);
    const eps = isZeroDecimalCurrency(currency) ? 0.5 : 0.009;

    if (!(valorLocal > 0)) {
      showWarning('Informe o valor pago');
      return;
    }
    if (maxLocal > 0 && valorLocal - maxLocal > eps) {
      showWarning(
        `O valor não pode ser maior que o restante (${formatPendenciaLocal(current)})`
      );
      return;
    }

    const valorBrl = localAmountToBrl(valorLocal, current);
    if (!(valorBrl > 0)) {
      showWarning('Não foi possível calcular o valor do pagamento');
      return;
    }

    try {
      setPayingPendencia(true);
      const response = await apiService.baixarPendenciaPagamento(current.id, {
        foto_pagamento: pendingPaymentPhoto,
        valor_brl: Number(valorBrl.toFixed(2)),
        codigo_acesso: withdrawalData.codigo_acesso || undefined,
        numero_documento: withdrawalData.numero_documento || undefined,
        codigo_cliente: withdrawalData.codigo_cliente || undefined,
      });
      const updated = response?.data || response;
      const quitada =
        updated?.quitada === true ||
        updated?.status === 'pago' ||
        updated?.status === 'cancelado';

      showSuccess(
        response?.message ||
          (quitada
            ? 'Pagamento da pendência registrado'
            : 'Pagamento parcial registrado')
      );

      setPendingPaymentPhoto(null);

      if (quitada) {
        const remaining = pendingPaymentGate.slice(1);
        setPendingPaymentGate(remaining);
        if (remaining.length === 0) {
          const refreshed = await refreshClientPackagesForWithdrawal();
          applyWithdrawalPaymentGates(
            refreshed?.pacotes || clientPackages,
            refreshed?.response || {}
          );
        } else {
          prefillPendingPaymentValor(remaining[0]);
        }
      } else {
        const nextPend = {
          ...current,
          ...updated,
          codigo_pacote: current.codigo_pacote,
          nome_destinatario: current.nome_destinatario,
        };
        setPendingPaymentGate([nextPend, ...pendingPaymentGate.slice(1)]);
        prefillPendingPaymentValor(nextPend);
        await refreshClientPackagesForWithdrawal();
      }
    } catch (error) {
      showError(error?.message || 'Erro ao registrar pagamento da pendência');
    } finally {
      setPayingPendencia(false);
    }
  };

  const handleQrLocalPayValor = raw => {
    const current = qrCheck?.financeiro?.pendencia_local;
    if (!current) return;
    const currency = pendingCurrency(current);
    const maxLocal = Number(current.valor_local) || 0;
    const next = formatPendingLocalInput(raw, currency);
    const parsed = parsePendingLocalInput(next, currency);
    const eps = isZeroDecimalCurrency(currency) ? 0.5 : 0.009;
    if (maxLocal > 0 && parsed - maxLocal > eps) {
      prefillPendingPaymentValor(current);
      return;
    }
    setPendingPaymentValor(next);
  };

  const patchQrLocalPendencias = (nextList) => {
    setPendingPaymentGate(nextList);
    setQrCheck(prev => {
      if (!prev?.financeiro) return prev;
      return {
        ...prev,
        financeiro: {
          ...prev.financeiro,
          pendencia_local: nextList[0] || null,
          pendencias_locais: nextList,
        },
      };
    });
  };

  const handleQrLocalPay = async () => {
    const locais = Array.isArray(qrCheck?.financeiro?.pendencias_locais) &&
      qrCheck.financeiro.pendencias_locais.length
      ? qrCheck.financeiro.pendencias_locais
      : qrCheck?.financeiro?.pendencia_local
        ? [qrCheck.financeiro.pendencia_local]
        : [];
    const current = locais[0];
    if (!current?.id) {
      showWarning('Nenhuma pendência para abater');
      return;
    }
    if (!(pendingPaymentPhoto instanceof File)) {
      showWarning('Fotografe o dinheiro recebido antes de continuar');
      return;
    }
    const currency = pendingCurrency(current);
    const maxLocal = Number(current.valor_local) || 0;
    const valorLocal = parsePendingLocalInput(pendingPaymentValor, currency);
    const eps = isZeroDecimalCurrency(currency) ? 0.5 : 0.009;
    if (!(valorLocal > 0)) {
      showWarning('Informe o valor recebido');
      return;
    }
    if (maxLocal > 0 && valorLocal - maxLocal > eps) {
      showWarning(
        `O valor não pode ser maior que o restante (${formatPendenciaLocal(current)})`
      );
      return;
    }
    const valorBrl = localAmountToBrl(valorLocal, current);
    if (!(valorBrl > 0)) {
      showWarning('Não foi possível calcular o valor do pagamento');
      return;
    }

    const codigoCliente =
      (tipoConsulta === 'cliente' ? code : qrCheck?.pacote?.codigo_cliente) || '';
    const numeroDocumento = invoiceData?.numero_documento || '';

    try {
      setPayingPendencia(true);
      const response = await apiService.baixarPendenciaPagamento(current.id, {
        foto_pagamento: pendingPaymentPhoto,
        valor_brl: Number(valorBrl.toFixed(2)),
        codigo_acesso: fullCode || undefined,
        numero_documento: numeroDocumento || undefined,
        codigo_cliente: codigoCliente || undefined,
      });
      const updated = response?.data || response;
      const quitada =
        updated?.quitada === true ||
        updated?.status === 'pago' ||
        updated?.status === 'cancelado';
      showSuccess(
        response?.message ||
          (quitada ? 'Pagamento abatido. Pode seguir.' : 'Pagamento parcial registrado')
      );
      setPendingPaymentPhoto(null);
      if (quitada) {
        const remaining = locais.slice(1);
        patchQrLocalPendencias(remaining);
        if (remaining[0]) prefillPendingPaymentValor(remaining[0]);
      } else {
        const nextPend = {
          ...current,
          ...updated,
          codigo_pacote: current.codigo_pacote,
          nome_destinatario: current.nome_destinatario,
        };
        patchQrLocalPendencias([nextPend, ...locais.slice(1)]);
        prefillPendingPaymentValor(nextPend);
      }
    } catch (error) {
      showError(error?.message || 'Erro ao registrar o dinheiro recebido');
    } finally {
      setPayingPendencia(false);
    }
  };

  const stopWithdrawalQrScanner = async () => {
    const instance = html5QrCodeRef.current;
    html5QrCodeRef.current = null;
    if (!instance) return;
    try {
      await instance.stop();
    } catch {
      /* já parado */
    }
    try {
      instance.clear();
    } catch {
      /* ignore */
    }
  };

  const stopWithdrawalPackageQrScanner = async () => {
    const instance = packageHtml5QrCodeRef.current;
    packageHtml5QrCodeRef.current = null;
    if (!instance) return;
    try {
      await instance.stop();
    } catch {
      /* já parado */
    }
    try {
      instance.clear();
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (
      !showWithdrawalModal ||
      showWithdrawalForm ||
      withdrawalInputMode !== 'qr' ||
      showPendingPaymentModal ||
      showCameraModal
    ) {
      stopWithdrawalQrScanner();
      return undefined;
    }

    let cancelled = false;
    setWithdrawalQrError(null);

    const start = async () => {
      await stopWithdrawalQrScanner();
      if (cancelled) return;

      const el = document.getElementById(WITHDRAWAL_QR_READER_ID);
      if (!el) return;

      const html5QrCode = new Html5Qrcode(WITHDRAWAL_QR_READER_ID);
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const edge = Math.min(viewfinderWidth, viewfinderHeight);
          const size = Math.max(160, Math.floor(edge * 0.72));
          return { width: size, height: size };
        },
      };

      try {
        await html5QrCode.start(
          { facingMode: 'environment' },
          config,
          decodedText => {
            if (cancelled || qrScanHandledRef.current) return;
            const extracted = extractClientCodeFromScan(decodedText);
            if (!extracted) {
              showWarning(
                'QR lido, mas não foi possível extrair o código do cliente.'
              );
              return;
            }
            qrScanHandledRef.current = true;
            void stopWithdrawalQrScanner();
            verifyWithdrawalClientRef.current?.(null, extracted);
          },
          () => undefined
        );
      } catch (err) {
        if (!cancelled) {
          console.error('QR scanner:', err);
          setWithdrawalQrError(
            'Câmera indisponível. Digite o código.'
          );
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      void stopWithdrawalQrScanner();
    };
  }, [
    showWithdrawalModal,
    showWithdrawalForm,
    withdrawalInputMode,
    withdrawalQrScannerNonce,
    showPendingPaymentModal,
    showCameraModal,
    showWarning,
  ]);

  useEffect(() => {
    if (
      !showWithdrawalModal ||
      !showWithdrawalForm ||
      withdrawalPackageQrVerified ||
      withdrawalPackageInputMode !== 'qr' ||
      !withdrawalPackageQrScanActive ||
      !isWithdrawalPackageStepEligible(clientPackages) ||
      freightPaymentHold.length > 0
    ) {
      void stopWithdrawalPackageQrScanner();
      return undefined;
    }

    let cancelled = false;
    setWithdrawalPackageQrError(null);

    const start = async () => {
      await stopWithdrawalPackageQrScanner();
      if (cancelled) return;

      const el = document.getElementById(WITHDRAWAL_PACKAGE_QR_READER_ID);
      if (!el) return;

      const html5QrCode = new Html5Qrcode(WITHDRAWAL_PACKAGE_QR_READER_ID);
      packageHtml5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const edge = Math.min(viewfinderWidth, viewfinderHeight);
          const size = Math.max(160, Math.floor(edge * 0.72));
          return { width: size, height: size };
        },
      };

      try {
        await html5QrCode.start(
          { facingMode: 'environment' },
          config,
          decodedText => {
            if (cancelled || packageQrScanHandledRef.current) return;
            const pkgs = clientPackagesRef.current;
            if (!scannedPackageMatchesList(decodedText, pkgs)) {
              showWarning(
                'O QR não confere com o número do pacote desta remessa.'
              );
              return;
            }
            packageQrScanHandledRef.current = true;
            void stopWithdrawalPackageQrScanner();
            setWithdrawalPackageQrVerified(true);
            setWithdrawalPackageQrError(null);
            showSuccess('Pacote conferido.');
          },
          () => undefined
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Package QR scanner:', err);
          setWithdrawalPackageQrError('Câmera indisponível. Digite o código.');
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      void stopWithdrawalPackageQrScanner();
    };
  }, [
    showWithdrawalModal,
    showWithdrawalForm,
    withdrawalPackageQrVerified,
    withdrawalPackageInputMode,
    withdrawalPackageQrScanActive,
    packageQrScannerNonce,
    clientPackages,
    freightPaymentHold.length,
    showWarning,
    showSuccess,
    withdrawalPackagesStepKey,
  ]);

  const handleSubmitReport = async () => {
    if (!selectedPackage || !reportData.tipo) {
      showWarning('Selecione o tipo de ocorrência');
      return;
    }

    const numeroDoc =
      invoiceData?.codigo_rastreamento || invoiceData?.numero_documento;
    if (!numeroDoc) {
      showError('Número do documento não encontrado');
      return;
    }

    // Usar o código de acesso que foi usado para verificar (fullCode)
    const codigoAcesso = fullCode.trim();
    if (!codigoAcesso) {
      showError('Código de acesso não encontrado');
      return;
    }

    try {
      setReportingPackage(true);
      await apiService.reportPackageOccurrence({
        pacote_id: selectedPackage.id,
        numero_documento: numeroDoc,
        codigo_acesso: codigoAcesso,
        tipo: reportData.tipo,
        descricao: reportData.descricao || undefined,
        fotos:
          reportData.fotos && reportData.fotos.length > 0
            ? reportData.fotos
            : undefined,
      });

      showSuccess('Ocorrência reportada com sucesso!');
      setShowReportModal(false);
      setSelectedPackage(null);
      setReportData({ tipo: '', descricao: '', fotos: [] });
    } catch (error) {
      console.error('Erro ao reportar ocorrência:', error);
      showError(error.message || 'Erro ao reportar ocorrência');
    } finally {
      setReportingPackage(false);
    }
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setWithdrawalFormData(prev => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const removeFile = field => {
    setWithdrawalFormData(prev => ({
      ...prev,
      [field]: null,
    }));
  };

  const getFilePreview = file => {
    if (!file) return null;
    return URL.createObjectURL(file);
  };

  // Funções para câmera
  const handleOpenCamera = async field => {
    await stopWithdrawalQrScanner();
    await stopWithdrawalPackageQrScanner();
    await stopCargaScanner();
    setCameraField(field);
    setShowCameraModal(true);
  };

  const handleCloseCamera = () => {
    setShowCameraModal(false);
    setCameraField(null);
    if (videoRef.current) {
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async (attempt = 0) => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        const prev = videoRef.current.srcObject;
        prev.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacingMode },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch {
          /* autoplay já coberto por muted + playsInline */
        }
      }
    } catch (error) {
      if (attempt < 1) {
        await new Promise(resolve => setTimeout(resolve, 350));
        return startCamera(attempt + 1);
      }
      console.error('Erro ao acessar câmera:', error);
      showError('Não foi possível acessar a câmera. Verifique as permissões.');
      handleCloseCamera();
    }
  };

  const toggleCamera = async () => {
    setCameraFacingMode(prev =>
      prev === 'environment' ? 'user' : 'environment'
    );
    // A câmera será reiniciada no useEffect quando cameraFacingMode mudar
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !cameraField) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Ajustar dimensões do canvas para o vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Desenhar frame do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Converter canvas para blob e depois para File
    canvas.toBlob(
      blob => {
        if (blob) {
          const file = new File(
            [blob],
            `foto_${cameraField}_${Date.now()}.jpg`,
            { type: 'image/jpeg' }
          );
          if (cameraField === 'foto_pendencia') {
            setPendingPaymentPhoto(file);
          } else {
            setWithdrawalFormData(prev => ({
              ...prev,
              [cameraField]: file,
            }));
          }
          showSuccess('Foto capturada com sucesso!');
          handleCloseCamera();
        }
      },
      'image/jpeg',
      0.9
    );
  };

  useEffect(() => {
    if (showCameraModal && videoRef.current) {
      startCamera();
    }
    return () => {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, [showCameraModal, cameraFacingMode]);

  // Funções para assinatura digital
  const handleSignatureStart = () => {
    setShowSignatureModal(true);
  };

  const handleSignatureClose = () => {
    setShowSignatureModal(false);
    if (signatureCanvasRef.current) {
      const ctx = signatureCanvasRef.current.getContext('2d');
      ctx.clearRect(
        0,
        0,
        signatureCanvasRef.current.width,
        signatureCanvasRef.current.height
      );
    }
  };

  const handleSignatureClear = () => {
    if (signatureCanvasRef.current) {
      const ctx = signatureCanvasRef.current.getContext('2d');
      ctx.clearRect(
        0,
        0,
        signatureCanvasRef.current.width,
        signatureCanvasRef.current.height
      );
    }
  };

  const handleSignatureConfirm = () => {
    if (!signatureCanvasRef.current) return;

    // Converter canvas para blob e depois para File
    signatureCanvasRef.current.toBlob(blob => {
      if (blob) {
        const file = new File([blob], 'assinatura.png', { type: 'image/png' });
        setWithdrawalFormData(prev => ({
          ...prev,
          foto_assinatura: file,
        }));
        setShowSignatureModal(false);
        showSuccess('Assinatura capturada com sucesso!');
      }
    }, 'image/png');
  };

  // Setup do canvas quando o modal abrir
  useEffect(() => {
    if (!showSignatureModal || !signatureCanvasRef.current) return;

    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let lastX = 0;
    let lastY = 0;
    let drawing = false;

    const getCoordinates = e => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x:
          ((e.clientX || (e.touches && e.touches[0].clientX)) - rect.left) *
          scaleX,
        y:
          ((e.clientY || (e.touches && e.touches[0].clientY)) - rect.top) *
          scaleY,
      };
    };

    const startDrawing = e => {
      e.preventDefault();
      drawing = true;
      const coords = getCoordinates(e);
      lastX = coords.x;
      lastY = coords.y;
    };

    const draw = e => {
      if (!drawing) return;
      e.preventDefault();

      const coords = getCoordinates(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      lastX = coords.x;
      lastY = coords.y;
    };

    const stopDrawing = () => {
      drawing = false;
    };

    // Eventos de mouse
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);

    // Eventos de touch
    canvas.addEventListener('touchstart', startDrawing);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('touchend', stopDrawing);
    canvas.addEventListener('touchcancel', stopDrawing);

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseout', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
      canvas.removeEventListener('touchcancel', stopDrawing);
    };
  }, [showSignatureModal]);

  const handleWithdrawalPackageManualConfirm = () => {
    const raw = withdrawalPackageManualCode.trim();
    if (!raw) {
      showWarning('Informe o número do pacote.');
      return;
    }
    if (!scannedPackageMatchesList(raw, clientPackages)) {
      showError('O código não confere com os pacotes desta remessa.');
      return;
    }
    setWithdrawalPackageQrVerified(true);
    setWithdrawalPackageQrError(null);
    showSuccess('Pacote conferido.');
  };

  const handleSubmitWithdrawal = async () => {
    if (freightHoldPackages(clientPackages).length > 0) {
      setShowFreightHoldModal(true);
      showWarning(
        'Carga está com pendência de pagamento. Contate quem enviou a carga.'
      );
      return;
    }

    if (!withdrawalPackageQrVerified) {
      showWarning(
        'Confirme o pacote com o QR ou o número antes de enviar as fotos.'
      );
      return;
    }

    if (!withdrawalFormData.foto_saida || !withdrawalFormData.foto_documento) {
      showWarning('Por favor, anexe a foto da carga e a foto do documento');
      return;
    }

    if (
      !withdrawalData.codigo_cliente ||
      !withdrawalData.codigo_acesso ||
      !withdrawalData.numero_documento
    ) {
      showError('Dados incompletos');
      return;
    }

    try {
      setSubmittingWithdrawal(true);
      await apiService.reportPackageWithdrawal({
        codigo_cliente: withdrawalData.codigo_cliente,
        numero_documento: withdrawalData.numero_documento,
        codigo_acesso: withdrawalData.codigo_acesso,
        foto_saida: withdrawalFormData.foto_saida,
        foto_assinatura: withdrawalFormData.foto_assinatura,
        foto_documento: withdrawalFormData.foto_documento,
      });

      showSuccess('Retirada registrada com sucesso!');
      setShowWithdrawalModal(false);
      setWithdrawalData({
        numero_documento: '',
        codigo_acesso: '',
        codigo_cliente: '',
      });
      setWithdrawalFormData({
        foto_saida: null,
        foto_assinatura: null,
        foto_documento: null,
      });
      setClientPackages([]);
      setShowWithdrawalForm(false);
      setFreightPaymentHold([]);
      setShowFreightHoldModal(false);
      setWithdrawalRemetente(null);
      setPendingPaymentGate([]);
      setShowPendingPaymentModal(false);
      setWithdrawalPackageQrVerified(false);
      setWithdrawalPackageQrError(null);
      setWithdrawalPackageInputMode('qr');
      setWithdrawalPackageManualCode('');
      packageQrScanHandledRef.current = false;
      setWithdrawalPackageQrScanActive(false);
      setCargaStep(null);
      setCargaScanError(null);
      setCargaMismatchOpen(false);
      setCargaInputMode('qr');
      setCargaManualCode('');
      cargaScanHandledRef.current = false;
    } catch (error) {
      console.error('Erro ao registrar retirada:', error);
      showError(error.message || 'Erro ao registrar retirada');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const cargaExpectedCodes = useMemo(() => {
    const fromItems = Array.isArray(qrCheck?.itens)
      ? qrCheck.itens.map(item => item?.codigo_pacote)
      : [];
    return [
      ...new Set(
        [qrCheck?.pacote?.codigo_pacote, boxData?.codigo, ...fromItems]
          .map(value => String(value || '').trim())
          .filter(Boolean)
      ),
    ];
  }, [qrCheck, boxData]);

  const stopCargaScanner = async () => {
    const instance = cargaQrRef.current;
    cargaQrRef.current = null;
    if (!instance) return;
    try {
      await instance.stop();
    } catch {
      /* já parado */
    }
    try {
      instance.clear();
    } catch {
      /* ignore */
    }
  };

  const confirmCargaLabel = raw => {
    const extracted = extractPackageCodeFromScan(raw);
    const matches = cargaExpectedCodes.some(expected =>
      codesEqualLoose(extracted, expected)
    );
    if (!matches) {
      cargaScanHandledRef.current = true;
      void stopCargaScanner();
      setCargaMismatchOpen(true);
      return;
    }
    cargaScanHandledRef.current = true;
    void stopCargaScanner();
    setCargaScanError(null);
    setCargaMismatchOpen(false);
    setWithdrawalPackageQrVerified(true);
    setCargaStep('photos');
    showSuccess('Carga conferida.');
  };

  const retryCargaScan = () => {
    // Libera uma nova leitura só depois do confirm.
    setCargaMismatchOpen(false);
    cargaScanHandledRef.current = false;
    if (cargaInputMode === 'qr') {
      setCargaScannerNonce(n => n + 1);
    }
  };

  const startCargaCheck = () => {
    const codigoCliente =
      (tipoConsulta === 'cliente' ? code : '') ||
      qrCheck?.pacote?.codigo_cliente ||
      '';
    setWithdrawalData({
      numero_documento: invoiceData?.numero_documento || '',
      codigo_acesso: getCodigoAcessoFromData(invoiceData) || '',
      codigo_cliente: String(codigoCliente || '').trim(),
    });
    setWithdrawalFormData({
      foto_saida: null,
      foto_assinatura: null,
      foto_documento: null,
    });
    setWithdrawalPackageQrVerified(false);
    setCargaScanError(null);
    setCargaMismatchOpen(false);
    setCargaManualCode('');
    setCargaInputMode('qr');
    cargaScanHandledRef.current = false;
    setCargaScannerNonce(n => n + 1);
    void stopCheckQrScanner();
    setCargaStep('alert');
  };

  useEffect(() => {
    if (cargaStep !== 'scan' || cargaInputMode !== 'qr') {
      void stopCargaScanner();
      return undefined;
    }

    let cancelled = false;
    setCargaScanError(null);

    const start = async () => {
      await stopCargaScanner();
      if (cancelled) return;
      const el = document.getElementById(CARGA_LABEL_QR_READER_ID);
      if (!el) return;

      const html5QrCode = new Html5Qrcode(CARGA_LABEL_QR_READER_ID);
      cargaQrRef.current = html5QrCode;
      const config = {
        fps: 10,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const edge = Math.min(viewfinderWidth, viewfinderHeight);
          const size = Math.max(160, Math.floor(edge * 0.72));
          return { width: size, height: size };
        },
      };

      try {
        await html5QrCode.start(
          { facingMode: 'environment' },
          config,
          decodedText => {
            if (cancelled || cargaScanHandledRef.current) return;
            confirmCargaLabel(decodedText);
          },
          () => undefined
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Check da carga:', err);
          setCargaScanError('Câmera indisponível. Digite o código da etiqueta.');
          setCargaInputMode('manual');
        }
      }
    };

    void start();
    return () => {
      cancelled = true;
      void stopCargaScanner();
    };
  }, [cargaStep, cargaInputMode, cargaScannerNonce]);

  // Se não há código e não está carregando, mostrar Check QR Code
  if (!code && !loading && !qrCheck && !error) {
    return (
      <div className='verify-container'>
        <div className='verify-header'>
          <div className='verify-logo'>
            <img
              src='/SENDING_TO.png'
              alt='SendingTo Express'
              className='verify-logo-image'
            />
            <h1>SendingTo Express</h1>
          </div>
        </div>

        <div className='verify-content'>
          <div className='verify-card'>
            <h2>Retirada</h2>
            <p className='check-qr-lead'>
              Aponte a câmera para o QR do pacote. Na busca manual, informe o
              código do cliente. Se o pacote fizer parte de uma remessa,
              percorremos as invoices.
            </p>
            {!showCheckQrScanner ? (
              <button
                type='button'
                className='btn btn-primary check-qr-btn'
                onClick={() => {
                  setCheckQrInputMode('qr');
                  setCheckQrManualCode('');
                  setCheckQrError(null);
                  checkQrHandledRef.current = false;
                  setCheckQrScannerNonce(n => n + 1);
                  setShowCheckQrScanner(true);
                }}
              >
                <HiIcons.HiQrcode size={22} />
                Check QR Code
              </button>
            ) : (
              <div className='check-qr-panel'>
                <div className='check-qr-mode-toggle'>
                  <button
                    type='button'
                    className={checkQrInputMode === 'qr' ? 'is-active' : ''}
                    onClick={() => setCheckQrInputMode('qr')}
                  >
                    Câmera
                  </button>
                  <button
                    type='button'
                    className={checkQrInputMode === 'manual' ? 'is-active' : ''}
                    onClick={() => {
                      void stopCheckQrScanner();
                      setCheckQrInputMode('manual');
                    }}
                  >
                    Digitar código
                  </button>
                </div>
                {checkQrInputMode === 'qr' ? (
                  <div className='withdrawal-qr-container check-qr-camera'>
                    <div id={CHECK_QR_READER_ID} className='withdrawal-qr-reader' />
                  </div>
                ) : (
                  <form onSubmit={handleCheckQrManualSubmit}>
                    <label className='check-qr-manual-label' htmlFor='check-qr-cliente'>
                      Código do cliente
                    </label>
                    <input
                      id='check-qr-cliente'
                      type='text'
                      value={checkQrManualCode}
                      onChange={e => setCheckQrManualCode(e.target.value)}
                      placeholder='Código do cliente'
                      className='check-qr-manual-input'
                      autoFocus
                    />
                    <button type='submit' className='btn btn-primary check-qr-btn'>
                      Conferir
                    </button>
                  </form>
                )}
                {checkQrError && (
                  <p className='check-qr-error'>{checkQrError}</p>
                )}
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => {
                    void stopCheckQrScanner();
                    setShowCheckQrScanner(false);
                  }}
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className='verify-container'>
        <div className='verify-loading'>
          <div className='loading-spinner'></div>
          <p>Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error || !qrCheck) {
    return (
      <div className='verify-container'>
        <div className='verify-header'>
          <div className='verify-logo'>
            <img
              src='/SENDING_TO.png'
              alt='SendingTo Express'
              className='verify-logo-image'
            />
            <h1>SendingTo Express</h1>
          </div>
        </div>
        <div className='verify-content'>
          <div className='verify-card'>
            <div className='verify-error'>
              <HiIcons.HiXCircle size={48} color='#ef4444' />
              <h2>Erro</h2>
              <p>{error || 'Pacote não encontrado'}</p>
              <button
                className='btn btn-primary'
                onClick={() => {
                  setError(null);
                  setQrCheck(null);
                  setBoxData(null);
                  setShowCheckQrScanner(false);
                  setSearchParams({});
                }}
                style={{ marginTop: '1rem' }}
              >
                Check QR Code
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='verify-container'>
      <div className='verify-header'>
        <div className='verify-logo'>
          <img
            src='/SENDING_TO.png'
            alt='SendingTo Express'
            className='verify-logo-image'
          />
          <h1>SendingTo Express</h1>
        </div>
      </div>

      <div className='verify-content'>
        <div className='verify-card'>
          <QrCheckResult
            qrCheck={qrCheck}
            boxData={boxData}
            invoiceData={invoiceData}
            localPay={{
              valor: pendingPaymentValor,
              photo: pendingPaymentPhoto,
              paying: payingPendencia,
              canSubmit:
                pendingPaymentPhoto instanceof File &&
                parsePendingLocalInput(
                  pendingPaymentValor,
                  pendingCurrency(qrCheck?.financeiro?.pendencia_local)
                ) > 0,
              submitLabel: (() => {
                const pend = qrCheck?.financeiro?.pendencia_local;
                const currency = pendingCurrency(pend);
                const typed = parsePendingLocalInput(pendingPaymentValor, currency);
                const maxLocal = Number(pend?.valor_local) || 0;
                const eps = isZeroDecimalCurrency(currency) ? 0.5 : 0.009;
                return typed + eps >= maxLocal ? 'Abater e continuar' : 'Registrar parcial';
              })(),
              onValorChange: handleQrLocalPayValor,
              onCamera: () => {
                void handleOpenCamera('foto_pendencia');
              },
              onFile: setPendingPaymentPhoto,
              onClearPhoto: () => setPendingPaymentPhoto(null),
              onSubmit: handleQrLocalPay,
            }}
            onCheckCarga={startCargaCheck}
            onNewScan={() => {
              void stopCheckQrScanner();
              void stopCargaScanner();
              setError(null);
              setQrCheck(null);
              setBoxData(null);
              setInvoiceData(null);
              setFullCode('');
              setShowCheckQrScanner(false);
              setCargaStep(null);
              setCargaScanError(null);
              setCargaInputMode('qr');
              setCargaManualCode('');
              cargaScanHandledRef.current = false;
              setSearchParams({});
            }}
          />
        </div>
      </div>

      {cargaStep ? (
        <div className='carga-modal' role='dialog' aria-modal='true' aria-label='Conferência da carga'>
          <div className='carga-modal__card'>
            <header className='carga-modal__head'>
              <div>
                <p className='carga-modal__eyebrow'>Check da Carga</p>
                <h2>Conferência da carga</h2>
              </div>
              <button
                type='button'
                className='carga-modal__close'
                aria-label='Fechar conferência'
                onClick={() => {
                  void stopCargaScanner();
                  setCargaPreviewUrl(null);
                  setCargaMismatchOpen(false);
                  setCargaStep(null);
                }}
              >
                <HiIcons.HiX size={22} />
              </button>
            </header>
            <section className='carga-check' aria-live='polite'>
              {cargaStep === 'alert' ? (
                <>
                  <div className='carga-check__alert' role='alert'>
                    <HiIcons.HiExclamationCircle size={28} />
                    <div>
                      <strong>Confira a carga antes de entregar</strong>
                      <p>
                        Compare as fotos e as quantidades com o que está na caixa.
                        Depois escaneie o QR da etiqueta.
                      </p>
                    </div>
                  </div>
                  <div className='carga-items'>
                    <h3>Itens ({Array.isArray(qrCheck?.itens) ? qrCheck.itens.length : 0})</h3>
                    {Array.isArray(qrCheck?.itens) && qrCheck.itens.length > 0 ? (
                      qrCheck.itens.map(item => {
                        const fotos = Array.isArray(item.fotos) ? item.fotos : [];
                        return (
                          <article key={`${item.pacote_id || 'p'}-${item.id}`} className='carga-item'>
                            <div className='carga-item__photos'>
                              {fotos.length > 0 ? (
                                fotos.map(foto => {
                                  const { full, thumb } = itemPhotoSources(foto);
                                  if (!thumb) return null;
                                  return (
                                    <button
                                      key={foto.id || thumb}
                                      type='button'
                                      className='carga-item__photo'
                                      onClick={() => full && setCargaPreviewUrl(full)}
                                    >
                                      <img src={thumb} alt={item.nome || 'Foto do item'} />
                                    </button>
                                  );
                                })
                              ) : (
                                <div className='carga-item__photo carga-item__photo--empty'>
                                  <HiIcons.HiCamera size={28} />
                                  <span>Sem foto</span>
                                </div>
                              )}
                            </div>
                            <div className='carga-item__info'>
                              <h4>{item.nome || 'Item sem nome'}</h4>
                              {item.codigo_pacote ? (
                                <p>{item.codigo_pacote}</p>
                              ) : null}
                              <strong>Qtd {formatItemQty(item.quantidade)}</strong>
                            </div>
                          </article>
                        );
                      })
                    ) : (
                      <p className='qr-check-empty'>Nenhum item cadastrado neste pacote.</p>
                    )}
                  </div>
                  <button
                    type='button'
                    className='btn btn-primary qr-check-again'
                    onClick={() => {
                      cargaScanHandledRef.current = false;
                      setCargaScanError(null);
                      setCargaInputMode('qr');
                      setCargaScannerNonce(n => n + 1);
                      setCargaStep('scan');
                    }}
                  >
                    <HiIcons.HiQrcode size={18} />
                    Escanear etiqueta da carga
                  </button>
                </>
              ) : null}

              {cargaStep === 'scan' ? (
                <div className='carga-check__scan'>
                  <h3>Check da Carga</h3>
                  <p>Aponte a câmera para o QR Code da etiqueta da carga.</p>
                  {cargaInputMode === 'qr' ? (
                    <>
                      <div className='carga-check__reader' id={CARGA_LABEL_QR_READER_ID} />
                      {cargaScanError && !cargaMismatchOpen ? (
                        <p className='carga-check__error' role='alert'>{cargaScanError}</p>
                      ) : null}
                      <button
                        type='button'
                        className='btn btn-secondary qr-check-again'
                        onClick={() => {
                          cargaScanHandledRef.current = false;
                          void stopCargaScanner();
                          setCargaInputMode('manual');
                        }}
                      >
                        Digitar código da etiqueta
                      </button>
                    </>
                  ) : (
                    <form
                      onSubmit={event => {
                        event.preventDefault();
                        confirmCargaLabel(cargaManualCode);
                      }}
                    >
                      <label className='check-qr-manual-label' htmlFor='carga-manual-code'>
                        Código da etiqueta
                      </label>
                      <input
                        id='carga-manual-code'
                        className='check-qr-manual-input'
                        value={cargaManualCode}
                        onChange={event => setCargaManualCode(event.target.value)}
                        placeholder='Código do pacote na etiqueta'
                        autoComplete='off'
                      />
                      <button type='submit' className='btn btn-primary qr-check-again'>
                        Conferir etiqueta
                      </button>
                    </form>
                  )}
                </div>
              ) : null}

              {cargaStep === 'photos' ? (
                <form
                  className='carga-check__photos'
                  onSubmit={event => {
                    event.preventDefault();
                    handleSubmitWithdrawal();
                  }}
                >
                  <h3>Fotos obrigatórias</h3>
                  <div className='upload-group'>
                    <label className='upload-label'>Foto da carga *</label>
                    <div className='upload-container'>
                      {withdrawalFormData.foto_saida ? (
                        <div className='upload-preview'>
                          <img src={getFilePreview(withdrawalFormData.foto_saida)} alt='Foto da carga' />
                          <div className='upload-actions'>
                            <button type='button' className='btn-remove' onClick={() => removeFile('foto_saida')}>
                              <HiIcons.HiX size={18} />
                              Remover
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className='photo-options'>
                          <button type='button' className='btn-camera' onClick={() => handleOpenCamera('foto_saida')}>
                            <HiIcons.HiCamera size={24} />
                            Tirar Foto
                          </button>
                          <span className='photo-divider'>ou</span>
                          <label className='btn-select-file'>
                            <HiIcons.HiCloudUpload size={24} />
                            Selecionar no Dispositivo
                            <input
                              type='file'
                              accept='image/*'
                              capture='environment'
                              onChange={event => handleFileChange('foto_saida', event.target.files?.[0])}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='upload-group'>
                    <label className='upload-label'>Foto do Documento *</label>
                    <div className='upload-container'>
                      {withdrawalFormData.foto_documento ? (
                        <div className='upload-preview'>
                          <img src={getFilePreview(withdrawalFormData.foto_documento)} alt='Foto do documento' />
                          <div className='upload-actions'>
                            <button type='button' className='btn-remove' onClick={() => removeFile('foto_documento')}>
                              <HiIcons.HiX size={18} />
                              Remover
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className='photo-options'>
                          <button type='button' className='btn-camera' onClick={() => handleOpenCamera('foto_documento')}>
                            <HiIcons.HiCamera size={24} />
                            Tirar Foto
                          </button>
                          <span className='photo-divider'>ou</span>
                          <label className='btn-select-file'>
                            <HiIcons.HiCloudUpload size={24} />
                            Selecionar no Dispositivo
                            <input
                              type='file'
                              accept='image/*'
                              capture='environment'
                              onChange={event => handleFileChange('foto_documento', event.target.files?.[0])}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='upload-group'>
                    <label className='upload-label'>Registrar Assinatura (opcional)</label>
                    <div className='upload-container'>
                      {withdrawalFormData.foto_assinatura ? (
                        <div className='upload-preview'>
                          <img src={getFilePreview(withdrawalFormData.foto_assinatura)} alt='Assinatura' />
                          <div className='upload-actions'>
                            <button type='button' className='btn-remove' onClick={() => removeFile('foto_assinatura')}>
                              <HiIcons.HiX size={18} />
                              Remover
                            </button>
                            <button type='button' className='btn-change' onClick={handleSignatureStart}>
                              <HiIcons.HiPencil size={18} />
                              Assinar Novamente
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button type='button' className='btn-signature carga-check__sign' onClick={handleSignatureStart}>
                          <HiIcons.HiPencil size={24} />
                          Assinar Digitalmente
                        </button>
                      )}
                    </div>
                  </div>
                  <button type='submit' className='btn btn-primary qr-check-again' disabled={submittingWithdrawal}>
                    {submittingWithdrawal ? 'Registrando...' : 'Registrar Retirada'}
                  </button>
                </form>
              ) : null}
            </section>
            {cargaPreviewUrl ? (
              <button
                type='button'
                className='qr-photo-preview'
                onClick={() => setCargaPreviewUrl(null)}
                aria-label='Fechar foto'
              >
                <img src={cargaPreviewUrl} alt='Foto do item' />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {cargaMismatchOpen ? (
        <div className='carga-mismatch' role='dialog' aria-modal='true' aria-labelledby='carga-mismatch-title'>
          <div className='carga-mismatch__card'>
            <div className='carga-mismatch__icon' aria-hidden>
              <HiIcons.HiExclamationCircle size={28} />
            </div>
            <h3 id='carga-mismatch-title'>QR não confere</h3>
            <p>O QR não confere com a etiqueta desta carga.</p>
            <button type='button' className='btn btn-primary' onClick={retryCargaScan}>
              Escanear novamente
            </button>
          </div>
        </div>
      ) : null}

      <div className='verify-footer'>
        <p>
          © {new Date().getFullYear()} SendingTo Express. Todos os direitos
          reservados.
        </p>
      </div>

      {/* Modal de Retirada - não fecha ao clicar fora */}
      {showWithdrawalModal && (
        <div className='modal-overlay modal-withdrawal'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h3 className='modal-header-title-with-icon'>
                <HiIcons.HiTruck size={24} />
                Retirada de Pacote
              </h3>
              <button
                className='btn-icon'
                onClick={() => {
                  void stopWithdrawalQrScanner();
                  setShowWithdrawalModal(false);
                  setWithdrawalData({
                    numero_documento: '',
                    codigo_acesso: '',
                    codigo_cliente: '',
                  });
                  setClientPackages([]);
                  setShowWithdrawalForm(false);
                  setFreightPaymentHold([]);
                  setShowFreightHoldModal(false);
                  setWithdrawalRemetente(null);
                  setPendingPaymentGate([]);
                  setShowPendingPaymentModal(false);
                  setVerificationMessage(null);
                  setVerificationMessageType(null);
                  qrScanHandledRef.current = false;
                  setWithdrawalInputMode('qr');
                  setWithdrawalQrError(null);
                  setWithdrawalQrScannerNonce(0);
                  void stopWithdrawalPackageQrScanner();
                  setWithdrawalPackageQrVerified(false);
                  setWithdrawalPackageQrError(null);
                  setWithdrawalPackageInputMode('qr');
                  setWithdrawalPackageManualCode('');
                  setPackageQrScannerNonce(0);
                  packageQrScanHandledRef.current = false;
                  setWithdrawalPackageQrScanActive(false);
                }}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            {!showWithdrawalForm ? (
              <div className='modal-body modal-body--withdrawal-verify'>
                <form
                  onSubmit={verifyWithdrawalClient}
                  className='withdrawal-verify-form'
                >
                  <div className='withdrawal-verify-form-inner withdrawal-verify-form-inner--wide'>
                    <div className='withdrawal-verify-header'>
                      <div className='withdrawal-verify-icon-wrap'>
                        <HiIcons.HiIdentification
                          size={44}
                          className='withdrawal-verify-icon'
                        />
                      </div>
                      <h4 className='withdrawal-verify-title'>
                        Verificar Cliente
                      </h4>
                      <p className='withdrawal-verify-desc'>
                        Escaneie o QR do cliente ou digite o código.
                      </p>
                    </div>

                    <div className='withdrawal-verify-body'>
                      {withdrawalInputMode === 'qr' && (
                        <div className='withdrawal-verify-panel withdrawal-verify-panel--qr'>
                          <div className='withdrawal-verify-qr-wrap'>
                            <div className='withdrawal-qr-container'>
                              <div
                                id={WITHDRAWAL_QR_READER_ID}
                                className='withdrawal-qr-reader'
                              />
                              {verifyingClient && (
                                <div
                                  className='withdrawal-qr-loading-overlay'
                                  aria-live='polite'
                                >
                                  <span className='loading-spinner' />
                                  <span>Verificando cliente…</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {withdrawalQrError && (
                            <p className='withdrawal-qr-error' role='alert'>
                              {withdrawalQrError}
                            </p>
                          )}
                          <button
                            type='button'
                            className='verify-action-btn verify-action-btn--outline'
                            onClick={() => {
                              qrScanHandledRef.current = false;
                              void stopWithdrawalQrScanner();
                              setWithdrawalInputMode('manual');
                            }}
                          >
                            <span className='verify-action-btn__icon'>
                              <HiIcons.HiPencil size={18} />
                            </span>
                            Digitar código manualmente
                          </button>
                        </div>
                      )}

                      {withdrawalInputMode === 'manual' && (
                        <div className='withdrawal-verify-panel withdrawal-verify-panel--manual'>
                          <span className='withdrawal-verify-eyebrow'>
                            Entrada manual
                          </span>
                          <p className='withdrawal-verify-hint'>
                            Informe o mesmo código exibido no documento ou no QR
                            do cliente.
                          </p>
                          <div className='withdrawal-verify-field'>
                            <label
                              htmlFor='withdrawal-codigo-cliente'
                              className='withdrawal-verify-field-label'
                            >
                              <HiIcons.HiUser size={18} aria-hidden />
                              Código do cliente
                            </label>
                            <input
                              id='withdrawal-codigo-cliente'
                              type='text'
                              value={withdrawalData.codigo_cliente}
                              onChange={e =>
                                setWithdrawalData(prev => ({
                                  ...prev,
                                  codigo_cliente: e.target.value,
                                }))
                              }
                              placeholder='Ex.: código alfanumérico do cliente'
                              required
                              className='withdrawal-verify-input'
                              autoComplete='off'
                              autoFocus
                            />
                          </div>
                          <div className='withdrawal-qr-manual-actions'>
                            <button
                              type='button'
                              className='verify-action-btn verify-action-btn--camera'
                              onClick={() => {
                                qrScanHandledRef.current = false;
                                setWithdrawalInputMode('qr');
                                setWithdrawalQrError(null);
                              }}
                            >
                              <span className='verify-action-btn__icon'>
                                <HiIcons.HiCamera size={18} />
                              </span>
                              Usar câmera
                            </button>
                            <button
                              type='submit'
                              className='verify-action-btn verify-action-btn--primary'
                              disabled={verifyingClient}
                            >
                              {verifyingClient ? (
                                <>
                                  <HiIcons.HiRefresh
                                    size={18}
                                    className='btn-icon-spin'
                                  />
                                  Verificando...
                                </>
                              ) : (
                                <>
                                  <span className='verify-action-btn__icon'>
                                    <HiIcons.HiSearch size={18} />
                                  </span>
                                  Verificar cliente
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            ) : (
              <div className='modal-withdrawal-body-footer-wrap'>
                <div className='modal-body'>
                  {/* Mensagem de retorno da verificação */}
                  {verificationMessage && (
                    <div
                      style={{
                        padding: '1rem',
                        borderRadius: '12px',
                        marginBottom: '1.5rem',
                        backgroundColor:
                          verificationMessageType === 'success'
                            ? '#d1fae5'
                            : verificationMessageType === 'error'
                              ? '#fee2e2'
                              : '#fef3c7',
                        border: `1px solid ${
                          verificationMessageType === 'success'
                            ? '#10b981'
                            : verificationMessageType === 'error'
                              ? '#ef4444'
                              : '#f59e0b'
                        }`,
                        color:
                          verificationMessageType === 'success'
                            ? '#065f46'
                            : verificationMessageType === 'error'
                              ? '#991b1b'
                              : '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      {verificationMessageType === 'success' && (
                        <HiIcons.HiCheckCircle size={24} />
                      )}
                      {verificationMessageType === 'error' && (
                        <HiIcons.HiXCircle size={24} />
                      )}
                      {verificationMessageType === 'warning' && (
                        <HiIcons.HiExclamationCircle size={24} />
                      )}
                      <p style={{ margin: 0, fontWeight: '500' }}>
                        {verificationMessage}
                      </p>
                    </div>
                  )}

                  {/* Mensagem quando não encontrou pacotes */}
                  {clientPackages.length === 0 &&
                    !verifyingClient &&
                    verificationMessageType !== 'error' && (
                      <div
                        style={{
                          padding: '2rem',
                          textAlign: 'center',
                          backgroundColor: '#f9fafb',
                          borderRadius: '12px',
                          border: '2px dashed #d1d5db',
                          marginBottom: '1.5rem',
                        }}
                      >
                        <HiIcons.HiSearch
                          size={48}
                          style={{ color: '#9ca3af', marginBottom: '1rem' }}
                        />
                        <h3
                          style={{ color: '#374151', marginBottom: '0.5rem' }}
                        >
                          Nenhum pacote encontrado
                        </h3>
                        <p style={{ color: '#6b7280', margin: 0 }}>
                          Não foram encontrados pacotes para os dados
                          informados. Verifique se o código do cliente, código
                          de acesso e número do documento estão corretos.
                        </p>
                      </div>
                    )}

                  {clientPackages.length > 0 && (
                    <div className='withdrawal-packages-section'>
                      {freightPaymentHold.length > 0 && (
                        <div className='freight-hold-banner' role='alert'>
                          <HiIcons.HiExclamationCircle size={22} />
                          <div>
                            <strong>Carga está com pendência de pagamento</strong>
                            <p>
                              Não é possível seguir com a entrega. Contate quem
                              enviou a carga
                              {withdrawalRemetente?.nome
                                ? ` (${withdrawalRemetente.nome})`
                                : ''}
                              {withdrawalRemetente?.telefone
                                ? ` · ${withdrawalRemetente.telefone}`
                                : ''}
                              .
                            </p>
                          </div>
                        </div>
                      )}
                      <div className='withdrawal-packages-section__head'>
                        <span className='withdrawal-packages-section__icon-wrap' aria-hidden>
                          <HiIcons.HiCube size={22} />
                        </span>
                        <div className='withdrawal-packages-section__titles'>
                          <h4 className='withdrawal-packages-section__title'>
                            Pacotes encontrados
                          </h4>
                          <p className='withdrawal-packages-section__subtitle'>
                            Dados da remessa para conferência na retirada
                          </p>
                        </div>
                        <span className='withdrawal-packages-section__badge'>
                          {clientPackages.length}
                        </span>
                      </div>
                      <div className='withdrawal-packages-list'>
                        {clientPackages.map((pkg, idx) => {
                          const statusInfo = getStatusInfo(pkg.status);
                          const statusLabel = statusInfo?.label || pkg.status;
                          const statusColor =
                            getStatusColor(pkg.status) || '#6b7280';
                          const isDelivered = pkg.status === 'entregue';
                          const codeDisplay =
                            pkg.codigo_pacote ||
                            pkg.codigo ||
                            pkg.id ||
                            '—';

                          return (
                            <article
                              key={pkg.id || idx}
                              className={`withdrawal-pkg-card${
                                isDelivered
                                  ? ' withdrawal-pkg-card--delivered'
                                  : ''
                              }`}
                            >
                              {isDelivered && (
                                <div className='withdrawal-pkg-card__ribbon'>
                                  <HiIcons.HiCheckCircle size={16} />
                                  Entregue
                                </div>
                              )}
                              <div className='withdrawal-pkg-card__top'>
                                <div className='withdrawal-pkg-card__code-block'>
                                  <span className='withdrawal-pkg-card__code-label'>
                                    Nº pacote
                                  </span>
                                  <span className='withdrawal-pkg-card__code'>
                                    {codeDisplay}
                                  </span>
                                </div>
                                <span
                                  className='withdrawal-pkg-card__status'
                                  style={{
                                    backgroundColor: `${statusColor}18`,
                                    color: statusColor,
                                    borderColor: `${statusColor}40`,
                                  }}
                                >
                                  {statusLabel}
                                </span>
                              </div>

                              <CaixaFisicaLocator
                                lookup={collectCaixaFisicaLookup([pkg])}
                                compact
                              />

                              <div className='withdrawal-pkg-card__grid'>
                                <div className='withdrawal-pkg-field'>
                                  <span className='withdrawal-pkg-field__label'>
                                    Destinatário
                                  </span>
                                  <span className='withdrawal-pkg-field__value'>
                                    {pkg.nome_destinatario || '—'}
                                  </span>
                                </div>
                                <div className='withdrawal-pkg-field'>
                                  <span className='withdrawal-pkg-field__label'>
                                    Telefone
                                  </span>
                                  <span className='withdrawal-pkg-field__value withdrawal-pkg-field__value--muted'>
                                    {pkg.telefone_destinatario || '—'}
                                  </span>
                                </div>
                              </div>

                              <div className='withdrawal-pkg-card__content'>
                                <span className='withdrawal-pkg-field__label'>
                                  Conteúdo
                                </span>
                                <p className='withdrawal-pkg-card__content-text'>
                                  {pkg.conteudo || '—'}
                                </p>
                              </div>

                              {(pkg.peso_bruto ||
                                pkg.peso_liquido ||
                                pkg.origem ||
                                pkg.destino) && (
                                <div className='withdrawal-pkg-meta'>
                                  {pkg.peso_bruto && (
                                    <div className='withdrawal-pkg-meta__item'>
                                      <span>Peso bruto</span>
                                      <strong>{pkg.peso_bruto} kg</strong>
                                    </div>
                                  )}
                                  {pkg.peso_liquido && (
                                    <div className='withdrawal-pkg-meta__item'>
                                      <span>Peso líquido</span>
                                      <strong>{pkg.peso_liquido} kg</strong>
                                    </div>
                                  )}
                                  {pkg.origem && (
                                    <div className='withdrawal-pkg-meta__item'>
                                      <span>Origem</span>
                                      <strong>{pkg.origem}</strong>
                                    </div>
                                  )}
                                  {pkg.destino && (
                                    <div className='withdrawal-pkg-meta__item'>
                                      <span>Destino</span>
                                      <strong>{pkg.destino}</strong>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className='withdrawal-pkg-payment'>
                                <div className='withdrawal-pkg-payment__head'>
                                  <span className='withdrawal-pkg-field__label'>
                                    Valores / pagamentos
                                  </span>
                                  <span
                                    className={`withdrawal-pkg-payment__badge withdrawal-pkg-payment__badge--${String(
                                      pkg.status_pagamento || 'Pendente'
                                    )
                                      .toLowerCase()
                                      .normalize('NFD')
                                      .replace(/[\u0300-\u036f]/g, '')}`}
                                  >
                                    {pkg.status_pagamento || 'Pendente'}
                                  </span>
                                </div>
                                <div className='withdrawal-pkg-payment__totals'>
                                  <div className='withdrawal-pkg-payment__total-item'>
                                    <span>Total</span>
                                    <strong>
                                      {formatBrlMoney(
                                        pkg.valor_total ?? pkg.valor_declarado
                                      )}
                                    </strong>
                                  </div>
                                  <div className='withdrawal-pkg-payment__total-item withdrawal-pkg-payment__total-item--paid'>
                                    <span>Pago</span>
                                    <strong>
                                      {formatBrlMoney(pkg.valor_pago)}
                                    </strong>
                                  </div>
                                  <div className='withdrawal-pkg-payment__total-item withdrawal-pkg-payment__total-item--rest'>
                                    <span>Restante</span>
                                    <strong>
                                      {formatBrlMoney(pkg.valor_restante)}
                                    </strong>
                                  </div>
                                </div>

                                {Array.isArray(pkg.pagamentos) &&
                                  pkg.pagamentos.length > 0 && (
                                    <div className='withdrawal-pkg-payment__list'>
                                      {pkg.pagamentos.map((pag, pagIdx) => (
                                        <div
                                          key={pag.id || pagIdx}
                                          className='withdrawal-pkg-payment__row'
                                        >
                                          <div className='withdrawal-pkg-payment__row-main'>
                                            <strong>
                                              {formatBrlMoney(pag.valor)}
                                            </strong>
                                            <span>
                                              {formatPaymentDate(
                                                pag.data_pagamento
                                              )}
                                              {pag.metodo
                                                ? ` · ${pag.metodo}`
                                                : ''}
                                            </span>
                                          </div>
                                          {Array.isArray(pag.comprovantes) &&
                                            pag.comprovantes.length > 0 && (
                                              <div className='withdrawal-pkg-payment__proofs'>
                                                {pag.comprovantes.map(
                                                  (comp, cIdx) => {
                                                    const url = normalizeImageUrl(
                                                      comp.url || comp
                                                    );
                                                    if (!url) return null;
                                                    const isImage =
                                                      /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(
                                                        url
                                                      );
                                                    return (
                                                      <button
                                                        key={cIdx}
                                                        type='button'
                                                        className='withdrawal-pkg-payment__proof'
                                                        title={
                                                          comp.nome ||
                                                          'Comprovante'
                                                        }
                                                        onClick={() =>
                                                          window.open(
                                                            url,
                                                            '_blank'
                                                          )
                                                        }
                                                      >
                                                        {isImage ? (
                                                          <img
                                                            src={url}
                                                            alt={
                                                              comp.nome ||
                                                              `Comprovante ${cIdx + 1}`
                                                            }
                                                          />
                                                        ) : (
                                                          <span>PDF</span>
                                                        )}
                                                      </button>
                                                    );
                                                  }
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                {Array.isArray(pkg.pendencias_pagamento) &&
                                  pkg.pendencias_pagamento.some(
                                    (pend) => pend.status === 'pago'
                                  ) && (
                                    <div className='withdrawal-pkg-payment__pendencias'>
                                      <span className='withdrawal-pkg-field__label'>
                                        Pagamento na retirada
                                      </span>
                                      {pkg.pendencias_pagamento
                                        .filter((pend) => pend.status === 'pago')
                                        .map((pend) => (
                                          <div
                                            key={pend.id}
                                            className='withdrawal-pkg-payment__pend-row'
                                          >
                                            <div className='withdrawal-pkg-payment__row-main'>
                                              <strong>
                                                {formatPendenciaLocal(pend)}
                                              </strong>
                                              <span>
                                                {formatPendenciaCurrencyCode(pend)}
                                                {pend.pago_em
                                                  ? ` · ${formatPaymentDate(
                                                      pend.pago_em
                                                    )}`
                                                  : ''}
                                              </span>
                                            </div>
                                            {pend.foto_pagamento_url && (
                                              <button
                                                type='button'
                                                className='withdrawal-pkg-payment__proof'
                                                title='Comprovante da pendência'
                                                onClick={() =>
                                                  window.open(
                                                    normalizeImageUrl(
                                                      pend.foto_pagamento_url
                                                    ),
                                                    '_blank'
                                                  )
                                                }
                                              >
                                                <img
                                                  src={normalizeImageUrl(
                                                    pend.foto_pagamento_url
                                                  )}
                                                  alt='Comprovante pagamento na retirada'
                                                />
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                    </div>
                                  )}

                                {pkg.tem_pendencia_pagamento &&
                                  pkg.pendencia_pagamento && (
                                    <div className='withdrawal-pkg-payment__open-pend'>
                                      <HiIcons.HiExclamationCircle size={16} />
                                      Pendência aberta:{' '}
                                      <strong>
                                        {formatPendenciaLocal(
                                          pkg.pendencia_pagamento
                                        )}
                                      </strong>
                                    </div>
                                  )}
                              </div>

                              {pkg.fotos &&
                                Array.isArray(pkg.fotos) &&
                                pkg.fotos.length > 0 && (
                                  <div className='withdrawal-pkg-fotos'>
                                    <span className='withdrawal-pkg-field__label'>
                                      Fotos ({pkg.fotos.length})
                                    </span>
                                    <div className='withdrawal-pkg-fotos__grid'>
                                      {pkg.fotos.map((foto, fotoIdx) => {
                                        const rawFotoUrl =
                                          typeof foto === 'string'
                                            ? foto
                                            : foto?.url || foto?.preview;
                                        if (!rawFotoUrl) return null;
                                        const fotoUrl =
                                          normalizeImageUrl(rawFotoUrl);

                                        return (
                                          <button
                                            key={fotoIdx}
                                            type='button'
                                            className='withdrawal-pkg-fotos__thumb'
                                            onClick={() =>
                                              window.open(fotoUrl, '_blank')
                                            }
                                          >
                                            <img
                                              src={fotoUrl}
                                              alt={`Foto ${fotoIdx + 1} do pacote ${codeDisplay}`}
                                              onError={e => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {clientPackages.length > 0 &&
                    isWithdrawalPackageStepEligible(clientPackages) &&
                    freightPaymentHold.length === 0 && (
                      <>
                        {!withdrawalPackageQrVerified ? (
                          <div className='withdrawal-package-confirm'>
                            <h4
                              style={{
                                margin: '0 0 0.5rem 0',
                                color: '#111827',
                                fontSize: '1.125rem',
                                fontWeight: 600,
                              }}
                            >
                              Confirmar pacote
                            </h4>
                            {withdrawalPackageInputMode === 'qr' &&
                              !withdrawalPackageQrScanActive && (
                                <p className='withdrawal-package-confirm-desc'>
                                  O QR deve corresponder ao{' '}
                                  <strong>Nº Pacote</strong> acima. Toque em
                                  Confirmar pacote para abrir a câmera e
                                  escanear.
                                </p>
                              )}
                            {withdrawalPackageInputMode === 'qr' &&
                              withdrawalPackageQrScanActive && (
                                <p className='withdrawal-package-confirm-desc'>
                                  Aponte a câmera para o QR da etiqueta.
                                </p>
                              )}
                            {withdrawalPackageInputMode === 'qr' &&
                              !withdrawalPackageQrScanActive && (
                                <>
                                  <button
                                    type='button'
                                    className='verify-action-btn verify-action-btn--primary'
                                    onClick={() => {
                                      setWithdrawalPackageQrError(null);
                                      packageQrScanHandledRef.current = false;
                                      setWithdrawalPackageQrScanActive(true);
                                      setPackageQrScannerNonce(n => n + 1);
                                    }}
                                  >
                                    <span className='verify-action-btn__icon'>
                                      <HiIcons.HiQrcode size={18} />
                                    </span>
                                    Confirmar pacote
                                  </button>
                                  <button
                                    type='button'
                                    className='verify-action-btn verify-action-btn--outline'
                                    onClick={() => {
                                      setWithdrawalPackageQrScanActive(false);
                                      void stopWithdrawalPackageQrScanner();
                                      setWithdrawalPackageInputMode('manual');
                                    }}
                                  >
                                    <span className='verify-action-btn__icon'>
                                      <HiIcons.HiPencil size={18} />
                                    </span>
                                    Digitar número do pacote
                                  </button>
                                </>
                              )}
                            {withdrawalPackageInputMode === 'qr' &&
                              withdrawalPackageQrScanActive && (
                                <>
                                  <div className='withdrawal-verify-qr-wrap'>
                                    <div className='withdrawal-qr-container'>
                                      <div
                                        id={WITHDRAWAL_PACKAGE_QR_READER_ID}
                                        className='withdrawal-qr-reader'
                                      />
                                    </div>
                                  </div>
                                  {withdrawalPackageQrError && (
                                    <p
                                      className='withdrawal-qr-error'
                                      role='alert'
                                    >
                                      {withdrawalPackageQrError}
                                    </p>
                                  )}
                                  <button
                                    type='button'
                                    className='verify-action-btn verify-action-btn--ghost'
                                    onClick={() => {
                                      packageQrScanHandledRef.current = false;
                                      void stopWithdrawalPackageQrScanner();
                                      setWithdrawalPackageQrScanActive(false);
                                      setWithdrawalPackageQrError(null);
                                      setPackageQrScannerNonce(n => n + 1);
                                    }}
                                  >
                                    <span className='verify-action-btn__icon'>
                                      <HiIcons.HiX size={18} />
                                    </span>
                                    Voltar
                                  </button>
                                  <button
                                    type='button'
                                    className='verify-action-btn verify-action-btn--outline'
                                    onClick={() => {
                                      packageQrScanHandledRef.current = false;
                                      void stopWithdrawalPackageQrScanner();
                                      setWithdrawalPackageQrScanActive(false);
                                      setWithdrawalPackageInputMode('manual');
                                    }}
                                  >
                                    <span className='verify-action-btn__icon'>
                                      <HiIcons.HiPencil size={18} />
                                    </span>
                                    Digitar número do pacote
                                  </button>
                                </>
                              )}
                            {withdrawalPackageInputMode === 'manual' && (
                              <>
                                <div className='withdrawal-verify-field'>
                                  <label
                                    htmlFor='withdrawal-package-manual'
                                    className='withdrawal-verify-field-label'
                                  >
                                    <HiIcons.HiCube size={18} aria-hidden />
                                    Nº do pacote
                                  </label>
                                  <input
                                    id='withdrawal-package-manual'
                                    type='text'
                                    value={withdrawalPackageManualCode}
                                    onChange={e =>
                                      setWithdrawalPackageManualCode(
                                        e.target.value
                                      )
                                    }
                                    placeholder='Igual ao da lista acima'
                                    className='withdrawal-verify-input'
                                    autoComplete='off'
                                  />
                                </div>
                                <div className='withdrawal-qr-manual-actions'>
                                  <button
                                    type='button'
                                    className='verify-action-btn verify-action-btn--camera'
                                    onClick={() => {
                                      packageQrScanHandledRef.current = false;
                                      setWithdrawalPackageInputMode('qr');
                                      setWithdrawalPackageQrError(null);
                                      setWithdrawalPackageQrScanActive(true);
                                      setPackageQrScannerNonce(n => n + 1);
                                    }}
                                  >
                                    <span className='verify-action-btn__icon'>
                                      <HiIcons.HiCamera size={18} />
                                    </span>
                                    Usar câmera
                                  </button>
                                  <button
                                    type='button'
                                    className='verify-action-btn verify-action-btn--primary'
                                    onClick={handleWithdrawalPackageManualConfirm}
                                  >
                                    <span className='verify-action-btn__icon'>
                                      <HiIcons.HiCheckCircle size={18} />
                                    </span>
                                    Confirmar pacote
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <form
                            id='withdrawal-form'
                            onSubmit={e => {
                              e.preventDefault();
                              handleSubmitWithdrawal();
                            }}
                          >
                        <div className='upload-section'>
                          <h4>Fotos Obrigatórias</h4>

                          {/* Foto da Saída */}
                          <div className='upload-group'>
                            <label className='upload-label'>
                              Foto da carga *
                            </label>
                            <div className='upload-container'>
                              {withdrawalFormData.foto_saida ? (
                                <div className='upload-preview'>
                                  <img
                                    src={getFilePreview(
                                      withdrawalFormData.foto_saida
                                    )}
                                    alt='Preview'
                                  />
                                  <div className='upload-actions'>
                                    <button
                                      type='button'
                                      className='btn-remove'
                                      onClick={() => removeFile('foto_saida')}
                                    >
                                      <HiIcons.HiX size={18} />
                                      Remover
                                    </button>
                                    <label className='btn-change'>
                                      <HiIcons.HiPencil size={18} />
                                      Trocar
                                      <input
                                        type='file'
                                        accept='image/*'
                                        onChange={e =>
                                          handleFileChange(
                                            'foto_saida',
                                            e.target.files?.[0]
                                          )
                                        }
                                        style={{ display: 'none' }}
                                      />
                                    </label>
                                  </div>
                                </div>
                              ) : (
                                <div className='photo-options'>
                                  <button
                                    type='button'
                                    className='btn-camera'
                                    onClick={() =>
                                      handleOpenCamera('foto_saida')
                                    }
                                  >
                                    <HiIcons.HiCamera size={24} />
                                    Tirar Foto
                                  </button>
                                  <span className='photo-divider'>ou</span>
                                  <label className='btn-select-file'>
                                    <HiIcons.HiCloudUpload size={24} />
                                    Selecionar no Dispositivo
                                    <input
                                      type='file'
                                      accept='image/*'
                                      capture='environment'
                                      onChange={e =>
                                        handleFileChange(
                                          'foto_saida',
                                          e.target.files?.[0]
                                        )
                                      }
                                      style={{ display: 'none' }}
                                    />
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Foto do Documento */}
                          <div className='upload-group'>
                            <label className='upload-label'>
                              Foto do Documento *
                            </label>
                            <div className='upload-container'>
                              {withdrawalFormData.foto_documento ? (
                                <div className='upload-preview'>
                                  <img
                                    src={getFilePreview(
                                      withdrawalFormData.foto_documento
                                    )}
                                    alt='Preview'
                                  />
                                  <div className='upload-actions'>
                                    <button
                                      type='button'
                                      className='btn-remove'
                                      onClick={() =>
                                        removeFile('foto_documento')
                                      }
                                    >
                                      <HiIcons.HiX size={18} />
                                      Remover
                                    </button>
                                    <label className='btn-change'>
                                      <HiIcons.HiPencil size={18} />
                                      Trocar
                                      <input
                                        type='file'
                                        accept='image/*'
                                        onChange={e =>
                                          handleFileChange(
                                            'foto_documento',
                                            e.target.files?.[0]
                                          )
                                        }
                                        style={{ display: 'none' }}
                                      />
                                    </label>
                                  </div>
                                </div>
                              ) : (
                                <div className='photo-options'>
                                  <button
                                    type='button'
                                    className='btn-camera'
                                    onClick={() =>
                                      handleOpenCamera('foto_documento')
                                    }
                                  >
                                    <HiIcons.HiCamera size={24} />
                                    Tirar Foto
                                  </button>
                                  <span className='photo-divider'>ou</span>
                                  <label className='btn-select-file'>
                                    <HiIcons.HiCloudUpload size={24} />
                                    Selecionar no Dispositivo
                                    <input
                                      type='file'
                                      accept='image/*'
                                      capture='environment'
                                      onChange={e =>
                                        handleFileChange(
                                          'foto_documento',
                                          e.target.files?.[0]
                                        )
                                      }
                                      style={{ display: 'none' }}
                                    />
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Registrar Assinatura */}
                          <div className='upload-group'>
                            <label className='upload-label'>
                              Registrar Assinatura (opcional)
                            </label>
                            <div className='upload-container'>
                              {withdrawalFormData.foto_assinatura ? (
                                <div className='upload-preview'>
                                  <img
                                    src={getFilePreview(
                                      withdrawalFormData.foto_assinatura
                                    )}
                                    alt='Preview'
                                  />
                                  <div className='upload-actions'>
                                    <button
                                      type='button'
                                      className='btn-remove'
                                      onClick={() =>
                                        removeFile('foto_assinatura')
                                      }
                                    >
                                      <HiIcons.HiX size={18} />
                                      Remover
                                    </button>
                                    <button
                                      type='button'
                                      className='btn-change'
                                      onClick={handleSignatureStart}
                                    >
                                      <HiIcons.HiPencil size={18} />
                                      Assinar Novamente
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className='signature-options'
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <button
                                    type='button'
                                    className='btn-signature'
                                    onClick={handleSignatureStart}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '12px 24px',
                                      backgroundColor: '#17A2B8',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    <HiIcons.HiPencil size={24} />
                                    Assinar Digitalmente
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                          </form>
                        )}
                      </>
                    )}
                </div>
                {/* Rodapé fixo com botão Registrar Retirada - só quando há pacotes para retirar */}
                {clientPackages.length > 0 &&
                  isWithdrawalPackageStepEligible(clientPackages) &&
                  freightPaymentHold.length === 0 &&
                  withdrawalPackageQrVerified && (
                    <div className='modal-footer-withdrawal'>
                      <button
                        type='submit'
                        form='withdrawal-form'
                        className='btn btn-primary'
                        disabled={submittingWithdrawal}
                      >
                        {submittingWithdrawal
                          ? 'Registrando...'
                          : 'Registrar Retirada'}
                      </button>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Câmera */}
      {showCameraModal && (
        <div className='modal-overlay camera-modal-overlay' onClick={handleCloseCamera}>
          <div
            className='modal-content camera-modal'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h3>Tirar Foto</h3>
              <button className='btn-icon' onClick={handleCloseCamera}>
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className='modal-body' style={{ padding: '1rem' }}>
              <div
                className='camera-container'
                style={{
                  position: 'relative',
                  width: '100%',
                  paddingBottom: '56.25%',
                  backgroundColor: '#000',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '1rem',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className='camera-video'
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div
                className='camera-actions'
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  padding: '0.5rem 0',
                }}
              >
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={handleCloseCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#6b7280';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiIcons.HiX size={18} />
                  Cancelar
                </button>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={toggleCamera}
                  title={
                    cameraFacingMode === 'environment'
                      ? 'Usar câmera frontal'
                      : 'Usar câmera traseira'
                  }
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#17A2B8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#138496';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#17A2B8';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiIcons.HiRefresh size={18} />
                  {cameraFacingMode === 'environment' ? 'Frontal' : 'Traseira'}
                </button>
                <button
                  type='button'
                  className='btn btn-primary btn-capture'
                  onClick={capturePhoto}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '140px',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#059669';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <HiIcons.HiCamera size={18} />
                  Capturar Foto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Assinatura Digital */}
      {showSignatureModal && (
        <div className='modal-overlay' onClick={handleSignatureClose}>
          <div
            className='modal-content signature-modal'
            onClick={e => e.stopPropagation()}
          >
            <div className='modal-header'>
              <h3>Assinatura Digital</h3>
              <button className='btn-icon' onClick={handleSignatureClose}>
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className='modal-body' style={{ padding: '1rem' }}>
              <div className='signature-container'>
                <p
                  className='signature-instruction'
                  style={{
                    margin: '0 0 1rem 0',
                    color: '#374151',
                    fontSize: '0.875rem',
                    textAlign: 'center',
                    fontWeight: '500',
                  }}
                >
                  Desenhe sua assinatura na área abaixo:
                </p>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                  }}
                >
                  <canvas
                    ref={signatureCanvasRef}
                    className='signature-canvas'
                    width={600}
                    height={300}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      height: '300px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      cursor: 'crosshair',
                      touchAction: 'none',
                    }}
                  />
                </div>
                <div
                  className='signature-actions'
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e5e7eb',
                  }}
                >
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={handleSignatureClear}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#6b7280',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      minWidth: '120px',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#4b5563';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = '#6b7280';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <HiIcons.HiX size={18} />
                    Limpar
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={handleSignatureConfirm}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#10b981',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      minWidth: '160px',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#059669';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow =
                        '0 6px 16px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = '#10b981';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    <HiIcons.HiCheck size={18} />
                    Confirmar Assinatura
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reportar Ocorrência */}
      {showReportModal && selectedPackage && (
        <div
          className='modal-overlay'
          onClick={() => setShowReportModal(false)}
        >
          <div className='modal-content' onClick={e => e.stopPropagation()}>
            <div className='modal-header'>
              <h3>
                Reportar Ocorrência - Pacote{' '}
                {selectedPackage.codigo_pacote ||
                  selectedPackage.codigo ||
                  selectedPackage.id}
              </h3>
              <button
                className='btn-icon'
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedPackage(null);
                  setReportData({ tipo: '', descricao: '', fotos: [] });
                }}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className='modal-body'>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleSubmitReport();
                }}
              >
                <div className='form-group'>
                  <label>Tipo de Ocorrência *</label>
                  <Select
                    value={reportData.tipo}
                    onChange={e =>
                      setReportData(prev => ({ ...prev, tipo: e.target.value }))
                    }
                    required
                    options={[
                      { value: '', label: 'Selecione...' },
                      { value: 'chegou', label: 'Chegou' },
                      { value: 'extraviado', label: 'Extraviado' },
                      { value: 'violado', label: 'Violado' },
                    ]}
                  />
                </div>
                <div className='form-group'>
                  <label>Descrição (opcional):</label>
                  <textarea
                    value={reportData.descricao}
                    onChange={e =>
                      setReportData(prev => ({
                        ...prev,
                        descricao: e.target.value,
                      }))
                    }
                    placeholder='Descreva a ocorrência...'
                    rows='4'
                  />
                </div>
                <div className='form-group'>
                  <label>Fotos (opcional):</label>
                  <div
                    className='photo-options'
                    style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                      marginBottom: '12px',
                    }}
                  >
                    <button
                      type='button'
                      className='btn-camera'
                      onClick={() => {
                        setCameraField('report_foto');
                        setShowCameraModal(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <HiIcons.HiCamera size={20} />
                      Tirar Foto
                    </button>
                    <span style={{ color: '#666', whiteSpace: 'nowrap' }}>
                      ou
                    </span>
                    <label
                      className='btn-select-file'
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <HiIcons.HiCloudUpload size={20} />
                      Selecionar no Dispositivo
                      <input
                        type='file'
                        accept='image/*'
                        multiple
                        onChange={async e => {
                          const files = Array.from(e.target.files || []);
                          const compressedFiles = await Promise.all(
                            files.map(async file => await compressImage(file))
                          );
                          setReportData(prev => ({
                            ...prev,
                            fotos: [...(prev.fotos || []), ...compressedFiles],
                          }));
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  {reportData.fotos && reportData.fotos.length > 0 && (
                    <div
                      className='photo-preview-list'
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginTop: '12px',
                      }}
                    >
                      {reportData.fotos.map((foto, idx) => (
                        <div
                          key={idx}
                          style={{
                            position: 'relative',
                            width: '100px',
                            height: '100px',
                          }}
                        >
                          <img
                            src={
                              foto instanceof File
                                ? URL.createObjectURL(foto)
                                : foto
                            }
                            alt={`Foto ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                          <button
                            type='button'
                            onClick={() => {
                              setReportData(prev => ({
                                ...prev,
                                fotos: prev.fotos.filter((_, i) => i !== idx),
                              }));
                            }}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <HiIcons.HiX size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={reportingPackage}
                >
                  {reportingPackage ? 'Reportando...' : 'Reportar Ocorrência'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ver Reportes */}
      {showViewReportsModal && selectedPackageForReports && (
        <div
          className='modal-overlay'
          onClick={() => setShowViewReportsModal(false)}
        >
          <div
            className='modal-content'
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className='modal-header'>
              <h3>
                Reportes - Pacote{' '}
                {selectedPackageForReports.codigo_pacote ||
                  selectedPackageForReports.codigo ||
                  selectedPackageForReports.id}
              </h3>
              <button
                className='btn-icon'
                onClick={() => {
                  setShowViewReportsModal(false);
                  setSelectedPackageForReports(null);
                }}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className='modal-body'>
              {selectedPackageForReports.reportes &&
              Array.isArray(selectedPackageForReports.reportes) &&
              selectedPackageForReports.reportes.length > 0 ? (
                <div className='reports-list'>
                  {selectedPackageForReports.reportes.map((reporte, index) => {
                    const tipoMap = {
                      chegou: 'Chegou',
                      extraviado: 'Extraviado',
                      violado: 'Violado',
                    };
                    const tipoLabel = tipoMap[reporte.tipo] || reporte.tipo;
                    const tipoColor =
                      {
                        chegou: '#10b981',
                        extraviado: '#ef4444',
                        violado: '#f59e0b',
                      }[reporte.tipo] || '#6b7280';

                    return (
                      <div
                        key={reporte.id || index}
                        className='reporte-item'
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px',
                          marginBottom: '16px',
                          backgroundColor: '#f9fafb',
                        }}
                      >
                        <div
                          className='reporte-header'
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                            }}
                          >
                            <span
                              style={{
                                padding: '4px 12px',
                                borderRadius: '6px',
                                backgroundColor: `${tipoColor}20`,
                                color: tipoColor,
                                fontSize: '14px',
                                fontWeight: '600',
                              }}
                            >
                              {tipoLabel}
                            </span>
                            {reporte.created_at && (
                              <span
                                style={{ fontSize: '12px', color: '#6b7280' }}
                              >
                                {new Date(reporte.created_at).toLocaleString(
                                  'pt-BR',
                                  {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  }
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        {reporte.descricao && (
                          <div
                            className='reporte-descricao'
                            style={{ marginBottom: '12px' }}
                          >
                            <strong>Descrição:</strong>
                            <p style={{ marginTop: '4px', color: '#374151' }}>
                              {reporte.descricao}
                            </p>
                          </div>
                        )}
                        {reporte.fotos &&
                          Array.isArray(reporte.fotos) &&
                          reporte.fotos.length > 0 && (
                            <div
                              className='reporte-fotos'
                              style={{ marginTop: '12px' }}
                            >
                              <strong>Fotos:</strong>
                              <div
                                className='photos-grid'
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns:
                                    'repeat(auto-fill, minmax(100px, 1fr))',
                                  gap: '8px',
                                  marginTop: '8px',
                                }}
                              >
                                {reporte.fotos.map((foto, fotoIndex) => {
                                  const rawFotoUrl =
                                    typeof foto === 'string'
                                      ? foto
                                      : foto?.url || foto?.preview;
                                  if (!rawFotoUrl) return null;
                                  const fotoUrl = normalizeImageUrl(rawFotoUrl);

                                  return (
                                    <div
                                      key={fotoIndex}
                                      className='photo-item'
                                      style={{ position: 'relative' }}
                                    >
                                      <img
                                        src={fotoUrl}
                                        alt={`Foto ${fotoIndex + 1} do reporte`}
                                        onClick={() =>
                                          window.open(fotoUrl, '_blank')
                                        }
                                        style={{
                                          cursor: 'pointer',
                                          width: '100%',
                                          height: '100px',
                                          objectFit: 'cover',
                                          borderRadius: '6px',
                                          border: '1px solid #e5e7eb',
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        <div
                          className='reporte-info'
                          style={{
                            marginTop: '12px',
                            fontSize: '12px',
                            color: '#6b7280',
                          }}
                        >
                          {reporte.numero_documento && (
                            <span>Documento: {reporte.numero_documento}</span>
                          )}
                          {reporte.codigo_acesso && (
                            <span style={{ marginLeft: '12px' }}>
                              Código: {reporte.codigo_acesso}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6b7280',
                  }}
                >
                  <HiIcons.HiInformationCircle
                    size={48}
                    style={{ marginBottom: '16px', opacity: 0.5 }}
                  />
                  <p>Nenhum reporte registrado para este pacote.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: pendência de pagamento na retirada */}
      {showPendingPaymentModal && pendingPaymentGate.length > 0 && (
        <div className='modal-overlay pending-payment-overlay'>
          <div
            className='pending-payment-modal'
            onClick={e => e.stopPropagation()}
            role='dialog'
            aria-modal='true'
            aria-labelledby='pending-payment-title'
          >
            <header className='ppm-hero'>
              <h2 id='pending-payment-title'>Pendência de pagamento</h2>
              <p className='ppm-alert' role='status'>
                O destinatário deve pagar para poder retirar a carga.
              </p>
            </header>

            <div className='ppm-body'>
              {(pendingPaymentGate[0]?.codigo_pacote ||
                pendingPaymentGate[0]?.nome_destinatario) && (
                <div className='ppm-meta'>
                  {pendingPaymentGate[0]?.codigo_pacote && (
                    <span className='ppm-meta-code'>
                      {pendingPaymentGate[0].codigo_pacote}
                    </span>
                  )}
                  {pendingPaymentGate[0]?.nome_destinatario && (
                    <span className='ppm-meta-name'>
                      {pendingPaymentGate[0].nome_destinatario}
                    </span>
                  )}
                </div>
              )}

              <div className='ppm-amount'>
                <span className='ppm-amount-label'>Valor a cobrar</span>
                <div className='ppm-amount-local'>
                  {formatPendenciaLocal(pendingPaymentGate[0])}
                </div>
                <span className='ppm-amount-currency'>
                  {formatPendenciaCurrencyCode(pendingPaymentGate[0])}
                </span>
                {pendingPaymentGate.length > 1 && (
                  <p className='ppm-amount-more'>
                    +{pendingPaymentGate.length - 1} pendência
                    {pendingPaymentGate.length > 2 ? 's' : ''} na fila
                  </p>
                )}
              </div>

              <div className='ppm-field'>
                <label htmlFor='pending-payment-valor'>
                  Valor deste pagamento ({pendingCurrencySymbol(pendingPaymentGate[0])})
                </label>
                <div className='ppm-field-input'>
                  <input
                    id='pending-payment-valor'
                    type='text'
                    inputMode='decimal'
                    value={pendingPaymentValor}
                    onChange={e => {
                      const currency = pendingCurrency(pendingPaymentGate[0]);
                      const maxLocal = Number(pendingPaymentGate[0]?.valor_local) || 0;
                      const next = formatPendingLocalInput(e.target.value, currency);
                      const parsed = parsePendingLocalInput(next, currency);
                      const eps = isZeroDecimalCurrency(currency) ? 0.5 : 0.009;
                      if (maxLocal > 0 && parsed - maxLocal > eps) {
                        prefillPendingPaymentValor(pendingPaymentGate[0]);
                        return;
                      }
                      setPendingPaymentValor(next);
                    }}
                    placeholder={formatPendingLocalInput('0', pendingCurrency(pendingPaymentGate[0])) || pendingCurrencySymbol(pendingPaymentGate[0])}
                    autoComplete='off'
                  />
                </div>
                <p className='ppm-field-hint'>
                  Pode alterar para menos · máximo{' '}
                  {formatPendenciaLocal(pendingPaymentGate[0])}
                </p>
              </div>

              <div className='ppm-photo'>
                <span className='ppm-photo-label'>Comprovante fotográfico</span>
                <div className='ppm-photo-actions'>
                  <button
                    type='button'
                    className='ppm-photo-btn ppm-photo-btn--camera'
                    onClick={() => {
                      void handleOpenCamera('foto_pendencia');
                    }}
                  >
                    <HiIcons.HiCamera size={20} aria-hidden />
                    Tirar foto
                  </button>
                  <label className='ppm-photo-btn ppm-photo-btn--upload'>
                    <HiIcons.HiCloudUpload size={20} aria-hidden />
                    Anexar
                    <input
                      type='file'
                      accept='image/*'
                      onChange={e => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) setPendingPaymentPhoto(file);
                      }}
                    />
                  </label>
                </div>
                {pendingPaymentPhoto && (
                  <div className='ppm-photo-preview'>
                    <img
                      src={URL.createObjectURL(pendingPaymentPhoto)}
                      alt='Comprovante do pagamento'
                    />
                    <div className='ppm-photo-preview-info'>
                      <strong>Foto anexada</strong>
                      <span>{pendingPaymentPhoto.name}</span>
                    </div>
                    <button
                      type='button'
                      className='ppm-photo-remove'
                      aria-label='Remover foto'
                      onClick={() => setPendingPaymentPhoto(null)}
                    >
                      <HiIcons.HiX size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <footer className='ppm-footer'>
              <button
                type='button'
                className='ppm-submit'
                disabled={
                  payingPendencia ||
                  !(pendingPaymentPhoto instanceof File) ||
                  !(
                    parsePendingLocalInput(
                      pendingPaymentValor,
                      pendingCurrency(pendingPaymentGate[0])
                    ) > 0
                  )
                }
                onClick={handleConfirmPendingPayment}
              >
                {payingPendencia
                  ? 'Registrando…'
                  : (() => {
                      const currency = pendingCurrency(pendingPaymentGate[0]);
                      const typed = parsePendingLocalInput(
                        pendingPaymentValor,
                        currency
                      );
                      const maxLocal =
                        Number(pendingPaymentGate[0]?.valor_local) || 0;
                      const eps = isZeroDecimalCurrency(currency) ? 0.5 : 0.009;
                      return typed + eps >= maxLocal
                        ? 'Confirmar e continuar'
                        : 'Registrar parcial';
                    })()}
              </button>
            </footer>
          </div>
        </div>
      )}

      {showFreightHoldModal && freightPaymentHold.length > 0 && (
        <div className='modal-overlay pending-payment-overlay'>
          <div
            className='pending-payment-modal freight-hold-modal'
            onClick={e => e.stopPropagation()}
            role='dialog'
            aria-modal='true'
            aria-labelledby='freight-hold-title'
          >
            <header className='ppm-hero'>
              <h2 id='freight-hold-title'>Carga com pendência de pagamento</h2>
              <p className='ppm-alert' role='status'>
                Esta carga não pode ser liberada até o pagamento ser efetuado.
              </p>
            </header>
            <div className='ppm-body'>
              <p className='freight-hold-copy'>
                Contate a pessoa que enviou a carga para quitar o pagamento.
                Depois disso, a entrega pode seguir.
              </p>
              {withdrawalRemetente?.nome && (
                <div className='freight-hold-contact'>
                  <span className='ppm-amount-label'>Quem enviou</span>
                  <strong>{withdrawalRemetente.nome}</strong>
                </div>
              )}
              <ul className='freight-hold-list'>
                {freightPaymentHold.map(pkg => (
                  <li key={pkg.id || pkg.codigo_pacote}>
                    <strong>{pkg.codigo_pacote || `CX #${pkg.id}`}</strong>
                    <span>
                      {pkg.status_pagamento || 'Pendente'}
                      {pkg.valor_restante != null
                        ? ` · resta ${formatBrlMoney(pkg.valor_restante)}`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <footer className='ppm-footer'>
              <button
                type='button'
                className='ppm-submit'
                onClick={() => setShowFreightHoldModal(false)}
              >
                Entendi
              </button>
            </footer>
          </div>
        </div>
      )}

      <div className='notifications-container'>
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Verify;
